

'use client';

import { z } from 'zod';
import { Timestamp, FieldValue, doc, addDoc, collection, updateDoc, getDoc, serverTimestamp, arrayUnion, arrayRemove, deleteDoc, getDocs, writeBatch, query, where, orderBy, FieldPath, setDoc, documentId, runTransaction, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';
import { app as firebaseApp, db as clientDb, auth as mainAuth, functions, storage } from '@/firebase/config';
import { httpsCallable } from 'firebase/functions';

import { sanitizeFirestoreData } from '@/lib/utils';
import { CARPETAS_CON_MINIATURA, MINIATURA_CALIDAD, MINIATURA_LADO, esMiniatura, rutaMiniatura } from '@/lib/miniaturas';
import type { JartinetHermandad, UserProfile, JoinRequest, SocialPost, SocialStory, Banda, Organizacion, Contract, SocialComment, PeriodicoArticle, Aviso, ModUpdate, FeaturedStory, HighlightStoryGroup, BankAccount, Transaction, Empresa, Carta, DiaSS, JartidexHistory, FixedTermDeposit, BankSettings, JartidexInvestment, PapeletaDeSitio, CityMapConfig, CityMapPin, CityMapPinCategory, ProgramaSSConfig, JarticardSobre, JarticardType, UserJarticard, PackProduct, SportsMatch, SportsBet, CecopProcesion, CecopStatus, JartiProduct, JartiMcIdentity, JartiShopConfig, JartiCatalogItem, GalleryEventDoc, PacksShopConfig, PacksPaymentInfo, PackPaymentMethod, PackOrderStatus, PackLedgerEntry, PackExpense, EstampitaDoc, CecopDia, MarchaDoc, MarchaEstado, MarchaOrigen, MarchaCategoria, MarchaProcesada, MarchasManifestState, SesionBanda, SesionPista } from '@/lib/types';
import { v4 as uuidv4 } from 'uuid';

// Helper to check for Admin role on the client side before attempting a write.
// This doesn't replace security rules, but provides a better user experience.
async function isClientAdmin(uid: string): Promise<boolean> {
    if (!uid) return false;
    try {
        const userDoc = await getDoc(doc(clientDb, 'users', uid));
        return userDoc.exists() && userDoc.data().role === 'admin';
    } catch (e) {
        return false;
    }
}

async function checkAdminOrManager(userId: string, entityRef: any, managerFields: string[]) {
    const [userDoc, entityDoc] = await Promise.all([
        getDoc(doc(clientDb, 'users', userId)),
        getDoc(entityRef)
    ]);

    if (!entityDoc.exists()) throw new Error("La entidad no existe.");
    
    const isAdmin = userDoc.exists() && userDoc.data()?.role === 'admin';
    if (isAdmin) return entityDoc.data();

    const entityData = entityDoc.data();
    
    for (const field of managerFields) {
        if (Array.isArray(entityData[field]) && entityData[field].includes(userId)) {
            return entityData;
        }
    }

    throw new Error("Permisos insuficientes. Debes ser administrador o gestor de esta entidad.");
}


const ApplicationFormSchema = z.object({
  email: z.string().email({ message: 'Por favor, introduce una dirección de correo válida.' }),
  pcRequirements: z.enum(['yes', 'no'], { required_error: 'Debes confirmar si cumples los requisitos.' }),
  minecraftKnowledge: z.string().min(1, { message: 'Debes seleccionar tu nivel de conocimiento.' }),
  modKnowledge: z.string().min(1, { message: 'Debes seleccionar tu conocimiento sobre mods.' }),
  serverActivity: z.string().min(1, { message: 'Debes seleccionar con qué frecuencia podrías entrar.' }),
  voiceConsent: z.enum(['yes', 'no'], { required_error: 'Debes confirmar tu consentimiento.' }),
  discordId: z.string().regex(/^\d{17,19}$/, { message: 'Por favor, introduce un ID de Discord válido.' }),
  minecraftId: z.string().min(3, { message: 'El ID de Minecraft debe tener al menos 3 caracteres.' }).max(16, { message: 'El ID de Minecraft no puede tener más de 16 caracteres.' }),
  username: z.string().min(2, { message: 'El ID de usuario debe tener al menos 2 caracteres.' }),
  name: z.string().min(2, { message: 'El nombre y apellidos son requeridos.' }),
  age: z.coerce.number({ invalid_type_error: 'Introduce tu edad.' }).int({ message: 'Introduce una edad válida.' }).min(14, { message: 'Debes tener al menos 14 años para solicitar acceso.' }).max(120, { message: 'Introduce una edad válida.' }),
  country: z.string().min(2, { message: 'Debes introducir tu país.' }),
  province: z.string().min(2, { message: 'Debes introducir tu provincia.' }),
  reason: z.string().min(50, { message: 'Tu razón debe tener al menos 50 caracteres.' }),
  source: z.string().min(1, { message: 'Debes seleccionar cómo nos conociste.' }),
  // Consentimiento RGPD: la casilla marcada envía 'on'. Sin marcar, la clave no llega
  // y este campo requerido falla (además de que el navegador bloquea el envío).
  privacyConsent: z.enum(['on', 'true'], { required_error: 'Debes aceptar la Política de Privacidad.', invalid_type_error: 'Debes aceptar la Política de Privacidad.' }),
});


export async function submitApplication(formData: FormData){
  const validatedFields = ApplicationFormSchema.safeParse(Object.fromEntries(formData.entries()));

  if (!validatedFields.success) {
    return {
      message: 'Por favor, corrige los errores del formulario.',
      errors: validatedFields.error.flatten().fieldErrors,
      success: false,
    };
  }

  try {
    const applicationData = {
      ...validatedFields.data,
      submissionDate: serverTimestamp(),
      status: 'pending' as const,
    };
    
    await addDoc(collection(clientDb, 'player_applications'), applicationData);

    return {
      message: '¡Solicitud enviada con éxito! La revisaremos pronto.',
      errors: undefined,
      success: true,
    };

  } catch (error) {
    console.error("Error submitting application:", error);
    return {
      message: 'Ha ocurrido un error inesperado al enviar tu solicitud. Por favor, inténtalo de nuevo.',
      errors: undefined,
      success: false,
    };
  }
}

export async function rejectApplication(applicationId: string) {
  try {
    const appRef = doc(clientDb, 'player_applications', applicationId);
    await updateDoc(appRef, { status: 'rejected' });
    return { success: true, message: 'Solicitud rechazada.' };
  } catch (error: any) {
    console.error("Error rejecting application:", error);
    return { success: false, error: 'No se pudo actualizar la solicitud.' };
  }
}

export const approveJoinRequest = async (hermandadId: string, targetUserId: string) => {
    const approveJoinRequestFn = httpsCallable(functions, 'approveJoinRequest');
    try {
        const result = await approveJoinRequestFn({ hermandadId, userId: targetUserId });
        return result.data as { success: boolean; message?: string; error?: string };
    } catch (e: any) {
        console.error("Error calling approveJoinRequest function:", e);
        return { success: false, message: e.message };
    }
};


export const updateUserProfile = async (updates: Partial<UserProfile>) => {
    const user = mainAuth.currentUser;
    if(!user) return { success: false, message: 'Debes iniciar sesión para actualizar tu perfil.' };

    try {
        const userRef = doc(clientDb, 'users', user.uid);
        await updateDoc(userRef, updates);
        return { success: true, message: 'Perfil actualizado con éxito.' };
    } catch(error: any) {
        console.error(`Error en updateUserProfile para ${user.uid}:`, error);
        return { success: false, message: `Error al actualizar el perfil: ${error.message}` };
    }
};

const NoticiaSchema = z.object({
  title: z.string().min(5, { message: 'El título debe tener al menos 5 caracteres.' }),
  content: z.string().min(10, { message: 'El contenido debe tener al menos 10 caracteres.' }),
  imageUrl: z.string().url("Debe ser una URL válida.").optional().or(z.literal('')),
});

export async function createNoticia(formData: FormData) {
    const validatedFields = NoticiaSchema.safeParse(Object.fromEntries(formData.entries()));
    if (!validatedFields.success) {
        return {
            message: 'Datos de la noticia no válidos.',
            errors: validatedFields.error.flatten().fieldErrors,
            success: false,
        };
    }

    try {
        await addDoc(collection(clientDb, 'noticias'), {
            ...validatedFields.data,
            date: serverTimestamp(),
        });
        return { success: true, message: 'Noticia publicada con éxito.' };
    } catch (error: any) {
        return { success: false, message: `Error al publicar la noticia: ${error.message}` };
    }
}

export async function updateNoticia(id: string, formData: FormData) {
    const validatedFields = NoticiaSchema.safeParse(Object.fromEntries(formData.entries()));
    if (!validatedFields.success) {
        return { success: false, message: "Datos inválidos.", errors: validatedFields.error.flatten().fieldErrors };
    }
    try {
        await updateDoc(doc(clientDb, 'noticias', id), validatedFields.data);
        return { success: true, message: 'Noticia actualizada con éxito.' };
    } catch (error) {
        return { success: false, message: 'Error al guardar la noticia.' };
    }
}

export async function deleteNoticia(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'noticias', id));
        return { success: true, message: 'Noticia eliminada.' };
    } catch (e) {
        return { success: false, error: 'No se pudo eliminar la noticia.' };
    }
}

const AvisoSchema = z.object({
  title: z.string().min(3, 'El título es requerido.'),
  content: z.string().min(10, 'El contenido es requerido.'),
});

export async function createAviso(formData: FormData) {
  const validatedFields = AvisoSchema.safeParse(Object.fromEntries(formData.entries()));

  if (!validatedFields.success) {
    return {
      message: 'Datos inválidos.',
      errors: validatedFields.error.flatten().fieldErrors,
      success: false,
    };
  }

  try {
    await addDoc(collection(clientDb, 'avisos'), {
      ...validatedFields.data,
      createdAt: serverTimestamp(),
    });
    return { success: true, message: 'Aviso creado con éxito.' };
  } catch (e: any) {
    return { success: false, message: `Error al crear el aviso: ${e.message}` };
  }
}

export async function updateAviso(id: string, formData: FormData) {
    const validatedFields = AvisoSchema.safeParse(Object.fromEntries(formData.entries()));

    if (!validatedFields.success) {
        return {
            message: 'Datos inválidos.',
            errors: validatedFields.error.flatten().fieldErrors,
            success: false,
        };
    }

    try {
        await updateDoc(doc(clientDb, 'avisos', id), validatedFields.data);
        return { success: true, message: 'Aviso actualizado con éxito.' };
    } catch (e: any) {
        return { success: false, message: `Error al actualizar el aviso: ${e.message}` };
    }
}

export async function deleteAviso(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'avisos', id));
        return { success: true, message: 'Aviso eliminado.' };
    } catch (e: any) {
        return { success: false, error: `No se pudo eliminar el aviso: ${e.message}` };
    }
}

const ModUpdateSchema = z.object({
    title: z.string().min(3, 'El título es requerido'),
    description: z.string().min(10, 'La descripción es requerida'),
    downloadUrl: z.string().url('Debe ser una URL válida').optional().or(z.literal('')),
});

export async function getAllModUpdates() {
    try {
        const q = query(collection(clientDb, 'mod_updates'), orderBy('date', 'desc'));
        const snapshot = await getDocs(q);
        const updates = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as ModUpdate[];
        return { updates };
    } catch (error: any) {
        console.error("Error fetching mod updates:", error);
        return { updates: [], error: `No se pudieron cargar las actualizaciones: ${error.message}` };
    }
}

export async function createModUpdate(formData: FormData) {
    const validatedFields = ModUpdateSchema.safeParse(Object.fromEntries(formData.entries()));
    if (!validatedFields.success) {
        return { success: false, message: "Datos inválidos.", errors: validatedFields.error.flatten().fieldErrors };
    }
    try {
        await addDoc(collection(clientDb, 'mod_updates'), { ...validatedFields.data, date: serverTimestamp() });
        return { success: true, message: 'Actualización del mod publicada.' };
    } catch (error: any) {
        return { success: false, message: `Error al publicar la actualización: ${error.message}` };
    }
}

export async function updateModUpdate(id: string, formData: FormData) {
    const validatedFields = ModUpdateSchema.safeParse(Object.fromEntries(formData.entries()));
    if (!validatedFields.success) {
        return { success: false, message: "Datos inválidos.", errors: validatedFields.error.flatten().fieldErrors };
    }
    try {
        await updateDoc(doc(clientDb, 'mod_updates', id), validatedFields.data);
        return { success: true, message: 'Actualización guardada.' };
    } catch (error: any) {
        return { success: false, message: `Error al guardar la actualización: ${error.message}` };
    }
}

export async function deleteModUpdate(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'mod_updates', id));
        return { success: true, message: 'Actualización eliminada.' };
    } catch (e: any) {
        return { success: false, error: `No se pudo eliminar la actualización: ${e.message}` };
    }
}

/**
 * Metadatos de TODA subida a Storage.
 *
 * <p>Sin esto, Firebase Storage sirve el fichero con `Cache-Control: private,
 * max-age=0`, o sea que **el navegador lo vuelve a descargar entero en cada
 * visita**: el mapa de la ciudad y la portada del libro (1,4 y 1,7 MB) se
 * bajaban enteros cada vez que alguien abría la portada. Era la línea más cara
 * de la factura con diferencia.</p>
 *
 * <p>Una semana es un compromiso seguro: casi todas las rutas llevan
 * `Date.now()` en el nombre, así que una imagen nueva es un fichero nuevo y no
 * hay nada que invalidar. Lo direccionado por hash (marchas, estampitas,
 * carillas del librito) lo escriben las Cloud Functions con `immutable`.</p>
 */
export const CACHE_SUBIDAS = { cacheControl: 'public, max-age=604800' };

// ── Compresión de imágenes ANTES de subirlas ────────────────────────────────
// La gente sube capturas de Minecraft en crudo: 1,5 MB de media en la red
// social y **2,3 MB por cromo** (y el álbum enseña 9 por hoja). Recomprimidas a
// WebP en el propio navegador se quedan en la décima parte, así que la web va
// más rápida en móvil y el egreso de Storage baja en la misma proporción.
//
// Se hace en el CLIENTE a propósito: no hay que subir el original para nada, y
// así tampoco se paga el tránsito de ida.

/** Solo estos formatos sabe rehacerlos el navegador con un canvas. */
const IMAGENES_RECOMPRIMIBLES = ['image/jpeg', 'image/png', 'image/webp'];
/** Por debajo de esto no merece la pena tocar nada (escudos, iconos…). */
const IMAGEN_MINIMA = 200 * 1024;

interface OpcionesImagen {
    /** Lado mayor máximo. `0` = no redimensionar (mapas: se miran con zoom 8×). */
    ladoMax?: number;
    /** 0-1. Con 1 el navegador guarda WebP sin pérdida. */
    calidad?: number;
    /**
     * Por debajo de este tamaño se devuelve el original tal cual. Las
     * miniaturas pasan `0`: ahí siempre hay que reescalar, por pequeño que sea
     * el fichero, porque lo que se busca son los píxeles, no los bytes. [165]
     */
    minimo?: number;
}

/**
 * Devuelve la imagen recomprimida, o **la original tal cual** si no se puede o
 * si no se gana nada. Nunca lanza: ante cualquier duda se sube lo que había.
 */
export async function comprimirImagen(
    entrada: File | Blob,
    { ladoMax = 1920, calidad = 0.85, minimo = IMAGEN_MINIMA }: OpcionesImagen = {},
): Promise<File | Blob> {
    try {
        if (!IMAGENES_RECOMPRIMIBLES.includes(entrada.type || '')) return entrada;
        if (entrada.size <= minimo) return entrada;

        const bitmap = await createImageBitmap(entrada);
        const escala = ladoMax > 0
            ? Math.min(1, ladoMax / Math.max(bitmap.width, bitmap.height))
            : 1;
        const ancho = Math.round(bitmap.width * escala);
        const alto = Math.round(bitmap.height * escala);

        const canvas = document.createElement('canvas');
        canvas.width = ancho;
        canvas.height = alto;
        const ctx = canvas.getContext('2d');
        if (!ctx) return entrada;
        ctx.drawImage(bitmap, 0, 0, ancho, alto);
        bitmap.close?.();

        const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/webp', calidad));
        // Si el navegador no sabe hacer WebP devuelve PNG, y ahí puede salir más
        // gordo que el original: en ese caso se queda el original.
        if (!blob || blob.size >= entrada.size) return entrada;

        const ext = blob.type === 'image/webp' ? 'webp' : 'png';
        const base = ((entrada as File).name || 'imagen').replace(/\.[^.]+$/, '');
        return new File([blob], `${base}.${ext}`, { type: blob.type });
    } catch {
        return entrada;
    }
}

/** Atajo para los sitios que trabajan con `File` y usan `file.name` en la ruta. */
async function paraSubir(file: File, opciones?: OpcionesImagen): Promise<File> {
    const r = await comprimirImagen(file, opciones);
    return r instanceof File ? r : file;
}

// ── Miniaturas [165] ────────────────────────────────────────────────────────
// Además del original de 1920 px se sube una copia de 600 px al lado, en
// `<ruta-sin-extensión>.thumb.webp`, que es lo que se pinta en las rejillas.
//
// EL TRUCO: la miniatura se sube con **el mismo token de descarga** que su
// original, así que el cliente deriva su URL de la del original con
// `urlMiniatura()` y NO hay que guardar nada nuevo en Firestore ni migrar los
// documentos que ya existen. Ver `src/lib/miniaturas.ts`.

/** Saca el `token` de una URL de descarga de Firebase Storage. */
function tokenDeUrl(url: string): string | null {
    const m = /[?&]token=([^&]+)/.exec(url);
    return m ? m[1] : null;
}

/**
 * Sube la miniatura de `origen` junto a `ruta`. **Nunca lanza y nunca bloquea
 * la subida buena**: si algo falla simplemente no hay miniatura, y el
 * componente `Image` se cae al original con su `onError`.
 */
async function subirMiniatura(ruta: string, origen: File | Blob, urlOriginal: string) {
    try {
        if (esMiniatura(ruta)) return;
        const carpeta = ruta.split('/')[0];
        if (!CARPETAS_CON_MINIATURA.includes(carpeta as any)) return;
        const token = tokenDeUrl(urlOriginal);
        if (!token) return;

        const mini = await comprimirImagen(origen, {
            ladoMax: MINIATURA_LADO, calidad: MINIATURA_CALIDAD, minimo: 0,
        });
        // Si devolvió la entrada tal cual es que no se pudo (formato raro) o que
        // no se ganaba nada: mejor no tener miniatura que tener una que pesa igual.
        if (mini === origen) return;

        await uploadBytes(ref(storage, rutaMiniatura(ruta)), mini, {
            ...CACHE_SUBIDAS,
            contentType: 'image/webp',
            customMetadata: { firebaseStorageDownloadTokens: token },
        });
    } catch {
        // silencio a propósito
    }
}

/**
 * Sube una imagen a `ruta` y, detrás, su miniatura. Devuelve la URL del
 * original, que es la que se guarda en Firestore como siempre.
 */
async function subirImagenConMiniatura(ruta: string, file: File): Promise<string> {
    const storageRef = ref(storage, ruta);
    await uploadBytes(storageRef, file, CACHE_SUBIDAS);
    const url = await getDownloadURL(storageRef);
    await subirMiniatura(ruta, file, url);
    return url;
}

// --- CITY MAP ---

async function uploadCityMapFile(file: File, path: string): Promise<string> {
    // Los MAPAS no se redimensionan y van sin pérdida: se miran con zoom hasta
    // 8× y se calibran mirando el píxel. Las fotos de las chinchetas sí.
    const esMapa = !path.includes('/pins/');
    const listo = await paraSubir(file, esMapa ? { ladoMax: 0, calidad: 1 } : undefined);
    const ruta = listo === file ? path : path.replace(/\.[^./]*$/, '') + '.webp';
    const storageRef = ref(storage, ruta);
    await uploadBytes(storageRef, listo, CACHE_SUBIDAS);
    return getDownloadURL(storageRef);
}

export async function getCityMapConfig() {
    try {
        const snap = await getDoc(doc(clientDb, 'city_map_config', 'main'));
        const config = snap.exists() ? (sanitizeFirestoreData({ id: snap.id, ...snap.data() }) as CityMapConfig) : null;
        return { config };
    } catch (error: any) {
        return { config: null, error: `No se pudo cargar el mapa: ${error.message}` };
    }
}

export async function getAllCityMapPins() {
    try {
        const q = query(collection(clientDb, 'city_map_pins'), orderBy('createdAt', 'asc'));
        const snapshot = await getDocs(q);
        const pins = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as CityMapPin[];
        return { pins };
    } catch (error: any) {
        return { pins: [], error: `No se pudieron cargar las chinchetas: ${error.message}` };
    }
}

export async function setCityMapImage(file: File) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const url = await uploadCityMapFile(file, `city_map/map_${Date.now()}_${file.name}`);
        await setDoc(doc(clientDb, 'city_map_config', 'main'), { mapImageUrl: url, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Mapa actualizado con éxito.', url };
    } catch (e: any) {
        return { success: false, message: `Error al subir el mapa: ${e.message}` };
    }
}

// ── Mapa del servidor (mod_map_config/main) ──
// Calibración px↔coordenadas MC para ubicar jugadores en tiempo real.
export async function setModServerMapImage(file: File) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const url = await uploadCityMapFile(file, `city_map/server_map_${Date.now()}_${file.name}`);
        await setDoc(doc(clientDb, 'mod_map_config', 'main'), { imageUrl: url, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Mapa del servidor actualizado.', url };
    } catch (e: any) {
        return { success: false, message: `Error al subir el mapa: ${e.message}` };
    }
}

export async function setModServerMapConfig(updates: { imageUrl?: string; calib?: { xPct: number; yPct: number; mcX: number; mcZ: number }[] }) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'mod_map_config', 'main'), { ...updates, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Calibración guardada.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// ── Iglesias del servidor (mod_churches/{id}) ──
export async function createChurch(data: any) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const ref = await addDoc(collection(clientDb, 'mod_churches'), { ...data, createdAt: serverTimestamp() });
        return { success: true, message: 'Iglesia creada.', id: ref.id };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}
export async function updateChurch(id: string, updates: any) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'mod_churches', id), { ...updates, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Iglesia actualizada.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}
export async function deleteChurch(id: string) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await deleteDoc(doc(clientDb, 'mod_churches', id));
        return { success: true, message: 'Iglesia eliminada.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// ── CECOP: procesiones con GPS (cecop_procesiones/{id}) ──
// Escritura directa de admin (regla `isAdmin()`), igual que mod_churches. El
// seguimiento en vivo (cecop_live) lo escribe modHeartbeat con el Admin SDK.

/** Limpia claves `undefined`: el cliente Firestore las rechaza (ver punto 62). */
function stripUndefined<T extends Record<string, any>>(o: T): T {
    Object.keys(o).forEach((k) => { if (o[k] === undefined) delete o[k]; });
    return o;
}

export async function createProcesion(data: Partial<CecopProcesion>) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const payload = stripUndefined({
            titulo: data.titulo || 'Procesión sin título',
            color: data.color || '#d4af37',
            descripcion: data.descripcion || '',
            hermandadId: data.hermandadId || '',
            numPasos: Number(data.numPasos) || 1,
            asignaciones: data.asignaciones || [],
            iglesia: data.iglesia ?? null,
            estacion: data.estacion ?? null,
            ida: data.ida || [],
            vuelta: data.vuelta || [],
            viewport: data.viewport ?? null,
            map: data.map ?? null,
            publicCard: false,
            youtubeUrl: '',
            status: 'draft' as const,
            // Token de la fuente de OBS: la URL es pública, así que el token es lo
            // único que protege el GPS. Se genera aquí y no se rota salvo a mano.
            obsToken: uuidv4().replace(/-/g, ''),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        const ref = await addDoc(collection(clientDb, 'cecop_procesiones'), payload);
        return { success: true, message: 'Procesión creada.', id: ref.id };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

export async function updateProcesion(id: string, updates: Partial<CecopProcesion>) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(
            doc(clientDb, 'cecop_procesiones', id),
            stripUndefined({ ...updates, updatedAt: serverTimestamp() } as any),
            { merge: true },
        );
        return { success: true, message: 'Procesión guardada.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

export async function deleteProcesion(id: string) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await deleteDoc(doc(clientDb, 'cecop_procesiones', id));
        // El doc de seguimiento se queda huérfano si el latido no lo pisa; se borra aquí.
        try { await deleteDoc(doc(clientDb, 'cecop_live', id)); } catch { /* puede no existir */ }
        return { success: true, message: 'Procesión eliminada.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// ── CECOP SEMANA SANTA: un día entero (cecop_dias/{diaId}) ──
// El id del documento ES el del día en `dias_ss`, así que no hace falta guardar
// una referencia ni puede haber dos días de CECOP para el mismo día del
// programa. Los recorridos NO se copian: se leen de `dias_ss` al pintar.

export async function guardarCecopDia(diaId: string, updates: Partial<CecopDia>) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    if (!diaId) return { success: false, message: 'Falta el día.' };
    try {
        const ref = doc(clientDb, 'cecop_dias', diaId);
        const snap = await getDoc(ref);
        const base: Record<string, any> = snap.exists()
            ? {}
            : {
                activo: false,
                hermandades: [],
                viewport: null,
                youtubeUrl: '',
                // Token de la fuente de OBS: la URL es pública y esto es lo
                // único que la protege. Se genera una vez y no se rota.
                obsToken: uuidv4().replace(/-/g, ''),
                createdAt: serverTimestamp(),
            };
        await setDoc(ref, stripUndefined({ ...base, ...updates, updatedAt: serverTimestamp() } as any), { merge: true });
        return { success: true, message: 'Día guardado.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

/**
 * ACTIVAR / DESACTIVAR un día: enciende el seguimiento GPS (el latido del mod
 * solo mira los días con `activo: true`) y saca el panel en la home pública.
 * Es un único interruptor a propósito: no tiene sentido publicar el panel sin
 * GPS ni gastar latido en un día que no ve nadie.
 */
export async function setCecopDiaActivo(diaId: string, activo: boolean) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const updates: Record<string, any> = { activo, updatedAt: serverTimestamp() };
        if (activo) updates.activadoAt = serverTimestamp();
        await setDoc(doc(clientDb, 'cecop_dias', diaId), updates, { merge: true });
        return { success: true, message: activo ? 'Día activado: ya se ve en la portada.' : 'Día desactivado.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

export async function borrarCecopDia(diaId: string) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await deleteDoc(doc(clientDb, 'cecop_dias', diaId));
        try { await deleteDoc(doc(clientDb, 'cecop_dia_live', diaId)); } catch { /* puede no existir */ }
        return { success: true, message: 'Seguimiento del día eliminado.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

/** Inicia / finaliza / devuelve a borrador una procesión. */
export async function setProcesionStatus(id: string, status: CecopStatus) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const updates: Record<string, any> = { status, updatedAt: serverTimestamp() };
        if (status === 'live') updates.startedAt = serverTimestamp();
        if (status === 'finished') updates.finishedAt = serverTimestamp();
        await setDoc(doc(clientDb, 'cecop_procesiones', id), updates, { merge: true });
        return {
            success: true,
            message: status === 'live' ? 'Procesión iniciada.' : status === 'finished' ? 'Procesión finalizada.' : 'Procesión en borrador.',
        };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// ── Librito del mod: render del libro a imágenes ──
// Regenera a mano las carillas (normalmente lo hace solo el repaso de cada 5
// minutos cuando detecta cambios en el programa).
export async function renderProgramaSSAhora() {
    try {
        const fn = httpsCallable(functions, 'renderProgramaSS');
        const res = await fn();
        const data = res.data as { success: boolean; hash: string; count: number };
        return { success: true, message: `Libro regenerado: ${data.count} carillas.`, hash: data.hash, count: data.count };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// ── Clima del servidor (mod_weather/main) ──
export async function setWeatherForecast(forecast: Record<string, string[]>) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'mod_weather', 'main'), { forecast, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Pronóstico guardado.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// ── Configuración de tiempo (mod_time_config/main) ──
export async function setTimeConfig(updates: { realTimeDay?: boolean }) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'mod_time_config', 'main'), { ...updates, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Configuración guardada.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// Nombre / descripción del mapa principal.
export async function setCityMapMeta(name: string, description: string) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'city_map_config', 'main'), { name, description, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Mapa principal actualizado.' };
    } catch (e: any) {
        return { success: false, message: `Error: ${e.message}` };
    }
}

// ── Mapas secundarios (city_maps/{id}) ──
export async function createSecondaryMap(name: string, description: string, file: File | null) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        let mapImageUrl = '';
        const docRef = await addDoc(collection(clientDb, 'city_maps'), {
            name, description, mapImageUrl: '', createdAt: serverTimestamp(),
        });
        if (file) {
            mapImageUrl = await uploadCityMapFile(file, `city_map/secondary/${docRef.id}_${Date.now()}_${file.name}`);
            await updateDoc(docRef, { mapImageUrl });
        }
        return { success: true, message: 'Mapa secundario creado.', id: docRef.id };
    } catch (e: any) {
        return { success: false, message: `Error al crear el mapa: ${e.message}` };
    }
}

export async function updateSecondaryMap(id: string, updates: { name?: string; description?: string }, file?: File | null) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const payload: any = { ...updates, updatedAt: serverTimestamp() };
        if (file) {
            payload.mapImageUrl = await uploadCityMapFile(file, `city_map/secondary/${id}_${Date.now()}_${file.name}`);
        }
        await updateDoc(doc(clientDb, 'city_maps', id), payload);
        return { success: true, message: 'Mapa secundario actualizado.' };
    } catch (e: any) {
        return { success: false, message: `Error al actualizar el mapa: ${e.message}` };
    }
}

export async function deleteSecondaryMap(id: string) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        // Borra el mapa y sus chinchetas.
        const pinsSnap = await getDocs(query(collection(clientDb, 'city_map_pins'), where('mapId', '==', id)));
        await Promise.all(pinsSnap.docs.map((d) => deleteDoc(d.ref)));
        await deleteDoc(doc(clientDb, 'city_maps', id));
        return { success: true, message: 'Mapa secundario eliminado.' };
    } catch (e: any) {
        return { success: false, message: `Error al eliminar el mapa: ${e.message}` };
    }
}

interface CityMapPinInput {
    name: string;
    category: CityMapPinCategory;
    description: string;
}

export async function createCityMapPin(data: CityMapPinInput, x: number, y: number, files: File[], mapId: string = 'main') {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const docRef = await addDoc(collection(clientDb, 'city_map_pins'), {
            name: data.name,
            category: data.category,
            description: data.description,
            x,
            y,
            mapId,
            images: [],
            createdAt: serverTimestamp(),
        });

        if (files.length > 0) {
            const urls = await Promise.all(
                files.slice(0, 2).map((file, i) => uploadCityMapFile(file, `city_map/pins/${docRef.id}/${i}_${file.name}`))
            );
            await updateDoc(docRef, { images: urls });
        }

        return { success: true, message: 'Chincheta añadida con éxito.', id: docRef.id };
    } catch (e: any) {
        return { success: false, message: `Error al crear la chincheta: ${e.message}` };
    }
}

export async function updateCityMapPin(id: string, data: CityMapPinInput, existingImages: string[], newFiles: File[]) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        let images = [...existingImages];
        if (newFiles.length > 0) {
            const slots = Math.max(0, 2 - images.length);
            const urls = await Promise.all(
                newFiles.slice(0, slots).map((file, i) => uploadCityMapFile(file, `city_map/pins/${id}/${Date.now()}_${i}_${file.name}`))
            );
            images = [...images, ...urls];
        }

        await updateDoc(doc(clientDb, 'city_map_pins', id), {
            name: data.name,
            category: data.category,
            description: data.description,
            images,
            updatedAt: serverTimestamp(),
        });
        return { success: true, message: 'Chincheta actualizada con éxito.' };
    } catch (e: any) {
        return { success: false, message: `Error al actualizar la chincheta: ${e.message}` };
    }
}

export async function updateCityMapPinPosition(id: string, x: number, y: number) {
    try {
        await updateDoc(doc(clientDb, 'city_map_pins', id), { x, y, updatedAt: serverTimestamp() });
        return { success: true };
    } catch (e: any) {
        return { success: false, message: `No se pudo mover la chincheta: ${e.message}` };
    }
}

export async function deleteCityMapPin(id: string, images: string[] = []) {
    try {
        await deleteDoc(doc(clientDb, 'city_map_pins', id));
        await Promise.all(images.map(async (url) => {
            try {
                await deleteObject(ref(storage, url));
            } catch {
                // El objeto puede no existir o ya estar borrado; lo ignoramos.
            }
        }));
        return { success: true, message: 'Chincheta eliminada.' };
    } catch (e: any) {
        return { success: false, error: `No se pudo eliminar la chincheta: ${e.message}` };
    }
}

const PeriodicoArticleSchema = z.object({
  title: z.string().min(5, { message: 'El titular debe tener al menos 5 caracteres.' }),
  subtitle: z.string().min(5, { message: 'El subtítulo es requerido.' }),
  authorName: z.string().min(3, { message: 'El nombre del autor es requerido.' }),
  category: z.string().min(3, { message: 'La categoría es requerida.' }),
  imageUrl: z.string().url({ message: 'Debe ser una URL de imagen válida.' }),
  content: z.string().min(50, { message: 'El contenido debe tener al menos 50 caracteres.' }),
  columns: z.coerce.number().min(1).max(3).optional().default(1),
});

export async function createPeriodicoArticle(formData: FormData) {
    const validatedFields = PeriodicoArticleSchema.safeParse(Object.fromEntries(formData.entries()));
    if (!validatedFields.success) {
        return { success: false, message: "Datos inválidos.", errors: validatedFields.error.flatten().fieldErrors };
    }
    try {
        await addDoc(collection(clientDb, 'periodico_articles'), { ...validatedFields.data, createdAt: serverTimestamp() });
        return { success: true, message: 'Artículo publicado con éxito.' };
    } catch (error: any) {
        return { success: false, message: `Error al publicar el artículo: ${error.message}` };
    }
}

export async function updatePeriodicoArticle(id: string, formData: FormData) {
    const validatedFields = PeriodicoArticleSchema.safeParse(Object.fromEntries(formData.entries()));
    if (!validatedFields.success) {
        return { success: false, message: "Datos inválidos.", errors: validatedFields.error.flatten().fieldErrors };
    }
    try {
        await updateDoc(doc(clientDb, 'periodico_articles', id), validatedFields.data);
        return { success: true, message: 'Artículo actualizado con éxito.' };
    } catch (error: any) {
        return { success: false, message: `Error al actualizar el artículo: ${error.message}` };
    }
}

export async function deletePeriodicoArticle(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'periodico_articles', id));
        return { success: true, message: 'Artículo eliminado.' };
    } catch (e: any) {
        return { success: false, error: `No se pudo eliminar el artículo: ${e.message}` };
    }
}

export async function markStoryAsViewed(storyId: string, userId: string) {
    try {
        const storyRef = doc(clientDb, 'social_stories', storyId);
        await updateDoc(storyRef, { viewedBy: arrayUnion(userId) });
    } catch (e) {
        console.error("Failed to mark story as viewed", e);
    }
}

export const requestToJoinHermandad = async (hermandadId: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "Debes iniciar sesión para unirte." };
    try {
        const requestRef = doc(clientDb, 'jartinet_hermandades', hermandadId, 'requests', user.uid);
        await setDoc(requestRef, {
            userId: user.uid,
            status: 'pending',
            solicitedAt: serverTimestamp(),
        });
        return { success: true, message: "Solicitud enviada con éxito." };
    } catch (e: any) {
        return { success: false, error: `No se pudo enviar la solicitud: ${e.message}` };
    }
};

export const rejectJoinRequest = async (hermandadId: string, userId: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "No autenticado." };
    try {
        // This is a client-side delete, which requires correct security rules
        const requestRef = doc(clientDb, 'jartinet_hermandades', hermandadId, 'requests', userId);
        await deleteDoc(requestRef);
        return { success: true, message: "Solicitud rechazada." };
    } catch (e: any) {
        return { success: false, error: `No se pudo rechazar la solicitud: ${e.message}` };
    }
};
export const removeBrother = async (hermandadId: string, memberId: string) => { return { success: false, error: "Not implemented" } };

export const createSocialPost = async (authorId: string, formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };

    try {
        const hermandadRef = doc(clientDb, 'jartinet_hermandades', authorId);
        const entityData = await checkAdminOrManager(user.uid, hermandadRef, ['hermanosMayores']);

        const postData: Partial<SocialPost> = {
            authorType: 'hermandad',
            authorId,
            authorName: entityData.nombreCompleto,
            authorShieldUrl: entityData.escudoUrl,
            creatorId: user.uid,
            title: formData.get('title') as string,
            content: formData.get('content') as string,
            mediaUrl: Array.from({ length: 10 }, (_, i) => formData.get(`mediaUrl-${i + 1}`) as string).filter(Boolean),
            mediaType: 'image', // Simplified, needs logic to detect video
            spotifyUrl: formData.get('spotifyUrl') as string || undefined,
            spotifyStartTime: formData.get('spotifyStartTime') ? parseInt(formData.get('spotifyStartTime') as string) : undefined,
            spotifyEndTime: formData.get('spotifyEndTime') ? parseInt(formData.get('spotifyEndTime') as string) : undefined,
            spotifyTrackName: formData.get('spotifyTrackName') as string || undefined,
            audioPreviewUrl: formData.get('audioPreviewUrl') as string || undefined,
            createdAt: serverTimestamp(),
            likes: [],
            comments: [],
            collaboratorId: formData.get('collaboratorId') as string === 'none' ? undefined : formData.get('collaboratorId') as string,
            collaborationStatus: formData.get('collaboratorId') as string === 'none' ? undefined : 'pending',
        };
        
        if (!postData.title || !postData.content) {
            return { success: false, message: "El título y el contenido son obligatorios."};
        }

        if(postData.collaboratorId) {
            const [type, id] = postData.collaboratorId.split(':');
            const collectionName = type === 'hermandad' ? 'jartinet_hermandades' : `${type}s`;
            const collaboratorSnap = await getDoc(doc(clientDb, collectionName, id));
            if(collaboratorSnap.exists()){
                const collaboratorData = collaboratorSnap.data();
                postData.collaboratorName = collaboratorData.nombreCompleto || collaboratorData.nombre;
                postData.collaboratorShieldUrl = collaboratorData.escudoUrl;
            }
        }


        Object.keys(postData).forEach(k => (postData as any)[k] === undefined && delete (postData as any)[k]);
        await addDoc(collection(clientDb, 'social_posts'), postData);
        
        return { success: true, message: "Publicación creada con éxito." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const deleteSocialPost = async (postId: string, authorId: string, authorType: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "No autenticado." };
    try {
        const collectionName = authorType === 'hermandad' ? 'jartinet_hermandades' : `${authorType}s`;
        const entityRef = doc(clientDb, collectionName, authorId);
        await checkAdminOrManager(user.uid, entityRef, ['hermanosMayores', 'directores', 'gestores']);
        await deleteDoc(doc(clientDb, 'social_posts', postId));
        return { success: true, message: 'Publicación eliminada.' };
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};

export const updateSocialPost = async (postId: string, formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };
    try {
        const postRef = doc(clientDb, 'social_posts', postId);
        const postSnap = await getDoc(postRef);
        if (!postSnap.exists()) return { success: false, message: "La publicación no existe." };
        const postData = postSnap.data();

        // Autorización: admin o gestor de la entidad autora.
        const collectionName = postData.authorType === 'hermandad' ? 'jartinet_hermandades' : `${postData.authorType}s`;
        const entityRef = doc(clientDb, collectionName, postData.authorId);
        await checkAdminOrManager(user.uid, entityRef, ['hermanosMayores', 'directores', 'gestores']);

        const title = formData.get('title') as string;
        const content = formData.get('content') as string;
        if (!title || !content) {
            return { success: false, message: "El título y el contenido son obligatorios." };
        }

        const mediaUrl = Array.from({ length: 10 }, (_, i) => formData.get(`mediaUrl-${i + 1}`) as string).filter(Boolean);

        const updates: Record<string, any> = {
            title,
            content,
            mediaUrl,
            spotifyUrl: formData.get('spotifyUrl') as string || '',
            spotifyTrackName: formData.get('spotifyTrackName') as string || '',
            audioPreviewUrl: formData.get('audioPreviewUrl') as string || '',
            spotifyStartTime: formData.get('spotifyStartTime') ? parseInt(formData.get('spotifyStartTime') as string) : null,
            spotifyEndTime: formData.get('spotifyEndTime') ? parseInt(formData.get('spotifyEndTime') as string) : null,
            updatedAt: serverTimestamp(),
        };

        await updateDoc(postRef, updates);
        return { success: true, message: "Publicación actualizada con éxito." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const createSocialStory = async (entityId: string, entityType: 'hermandad' | 'banda' | 'organizacion' | 'empresa', formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };

    const mediaUrl = formData.get('mediaUrl') as string;
    if (!mediaUrl) return { success: false, message: "Se requiere una URL de medio." };

    try {
        const collectionName = entityType === 'hermandad' ? 'jartinet_hermandades' : `${entityType}s`;
        const entityRef = doc(clientDb, collectionName, entityId);
        const entityData = await checkAdminOrManager(user.uid, entityRef, ['hermanosMayores', 'directores', 'gestores']);
        
        const isVideo = mediaUrl.includes('.mp4') || mediaUrl.includes('.mov');

        const storyData = {
            authorType: entityType,
            authorId: entityId,
            authorName: entityData.nombreCompleto || (entityData as JartinetHermandad).nombre,
            authorShieldUrl: entityData.escudoUrl,
            mediaUrl,
            mediaType: isVideo ? 'video' : 'image',
            text: formData.get('text') as string,
            spotifyUrl: formData.get('spotifyUrl') as string || undefined,
            spotifyStartTime: formData.get('spotifyStartTime') ? parseInt(formData.get('spotifyStartTime') as string) : undefined,
            spotifyEndTime: formData.get('spotifyEndTime') ? parseInt(formData.get('spotifyEndTime') as string) : undefined,
            spotifyTrackName: formData.get('spotifyTrackName') as string || undefined,
            audioPreviewUrl: formData.get('audioPreviewUrl') as string || undefined,
            createdAt: serverTimestamp(),
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
            viewedBy: [user.uid],
        };
        Object.keys(storyData).forEach(k => (storyData as any)[k] === undefined && delete (storyData as any)[k]);
        await addDoc(collection(clientDb, 'social_stories'), storyData);
        return { success: true, message: "Historia publicada con éxito." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};
export const inviteToCollaborate = async (formData: FormData) => { 
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };
    
    const postId = formData.get('postId') as string;
    const collaboratorId = formData.get('collaboratorId') as string;
    
    try {
        const postRef = doc(clientDb, 'social_posts', postId);
        const postSnap = await getDoc(postRef);

        if(!postSnap.exists()) throw new Error("La publicación no existe.");

        const postData = postSnap.data();
        const authorRef = doc(clientDb, postData.authorType === 'hermandad' ? 'jartinet_hermandades' : `${postData.authorType}s`, postData.authorId);
        await checkAdminOrManager(user.uid, authorRef, ['hermanosMayores', 'directores', 'gestores']);

        const [type, id] = collaboratorId.split(':');
        const collectionName = type === 'hermandad' ? 'jartinet_hermandades' : `${type}s`;
        const collaboratorSnap = await getDoc(doc(clientDb, collectionName, id));
        if(!collaboratorSnap.exists()) throw new Error("El colaborador no existe.");
        
        const collaboratorData = collaboratorSnap.data();
        
        await updateDoc(postRef, {
            collaboratorId,
            collaboratorName: collaboratorData.nombreCompleto || collaboratorData.nombre,
            collaboratorShieldUrl: collaboratorData.escudoUrl,
            collaborationStatus: 'pending',
        });
        return { success: true, message: "Invitación enviada." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const acceptCollaboration = async (postId: string) => {
    try {
        const postRef = doc(clientDb, 'social_posts', postId);
        await updateDoc(postRef, { collaborationStatus: 'accepted' });
        return { success: true, message: 'Colaboración aceptada.' };
    } catch (e: any) {
        return { success: false, error: 'No se pudo aceptar la colaboración.' };
    }
};
export const rejectCollaboration = async (postId: string) => {
    try {
        const postRef = doc(clientDb, 'social_posts', postId);
        await updateDoc(postRef, {
            collaborationStatus: 'rejected',
            collaboratorId: null,
            collaboratorName: null,
            collaboratorShieldUrl: null,
        });
        return { success: true, message: 'Colaboración rechazada.' };
    } catch (e: any) {
        return { success: false, error: 'No se pudo rechazar la colaboración.' };
    }
};
export const toggleLikePost = async (postId: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "No autenticado." };
    try {
        const postRef = doc(clientDb, 'social_posts', postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
            const postData = postSnap.data();
            const hasLiked = postData.likes?.includes(user.uid);
            await updateDoc(postRef, {
                likes: hasLiked ? arrayRemove(user.uid) : arrayUnion(user.uid)
            });
            return { success: true };
        }
        return { success: false, error: 'La publicación no existe.' };
    } catch (e: any) {
        return { success: false, error: 'No se pudo actualizar el "me gusta".' };
    }
};

export const createContract = async (formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };

    try {
        const emitterId = formData.get('emitterId') as string;
        const recipientId = formData.get('recipientId') as string;
        
        const [emitterType, emitterDocId] = emitterId.split(':');
        const [recipientType, recipientDocId] = recipientId.split(':');

        const emitterCollectionName = emitterType === 'hermandad' ? 'jartinet_hermandades' : `${emitterType}s`;
        const recipientCollectionName = recipientType === 'hermandad' ? 'jartinet_hermandades' : `${recipientType}s`;

        const emitterRef = doc(clientDb, emitterCollectionName, emitterDocId);
        const recipientRef = doc(clientDb, recipientCollectionName, recipientDocId);

        const [emitterSnap, recipientSnap] = await Promise.all([getDoc(emitterRef), getDoc(recipientRef)]);
        
        const emitterData = await checkAdminOrManager(user.uid, emitterRef, ['hermanosMayores', 'directores', 'gestores']);

        if (!recipientSnap.exists()) throw new Error("El destinatario no existe.");
        const recipientData = recipientSnap.data();

        const contractData = {
            emitterId,
            recipientId,
            emitterName: emitterData.nombreCompleto,
            emitterShieldUrl: emitterData.escudoUrl,
            recipientName: recipientData.nombreCompleto,
            recipientShieldUrl: recipientData.escudoUrl,
            concept: formData.get('concept') as string,
            content: formData.get('content') as string,
            status: 'pending' as const,
            createdAt: serverTimestamp(),
        };

        await addDoc(collection(clientDb, 'contracts'), contractData);
        return { success: true, message: "Contrato enviado con éxito." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};
export const signContract = async (contractId: string, recipientId: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "No autenticado." };

    try {
        const contractRef = doc(clientDb, 'contracts', contractId);
        const [recipientType, recipientDocId] = recipientId.split(':');
        const recipientCollectionName = recipientType === 'hermandad' ? 'jartinet_hermandades' : `${recipientType}s`;
        const recipientEntityRef = doc(clientDb, recipientCollectionName, recipientDocId);

        await checkAdminOrManager(user.uid, recipientEntityRef, ['hermanosMayores', 'directores', 'gestores']);

        await updateDoc(contractRef, {
            status: 'signed',
            signedAt: serverTimestamp(),
        });
        return { success: true, message: "Contrato firmado con éxito." };
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};
export const rejectContract = async (contractId: string, recipientId: string) => {
     const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "No autenticado." };
    try {
        const [recipientType, recipientDocId] = recipientId.split(':');
        const recipientCollectionName = recipientType === 'hermandad' ? 'jartinet_hermandades' : `${recipientType}s`;
        const recipientEntityRef = doc(clientDb, recipientCollectionName, recipientDocId);
        await checkAdminOrManager(user.uid, recipientEntityRef, ['hermanosMayores', 'directores', 'gestores']);
        
        const contractRef = doc(clientDb, 'contracts', contractId);
        await updateDoc(contractRef, { status: 'rejected' });
        return { success: true, message: "Contrato rechazado." };
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};

export const deleteSocialStory = async (storyId: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "No autenticado." };
    try {
        await deleteDoc(doc(clientDb, 'social_stories', storyId));
        return { success: true, message: 'Historia eliminada.' };
    } catch (e: any) {
        return { success: false, error: 'No se pudo eliminar la historia.' };
    }
};
export const addCommentToPost = async (formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };
    
    const postId = formData.get('postId') as string;
    const commentText = formData.get('commentText') as string;
    if (!commentText.trim()) return { success: false, message: "El comentario no puede estar vacío." };

    try {
        const userDoc = await getDoc(doc(clientDb, 'users', user.uid));
        if (!userDoc.exists()) return { success: false, message: "Perfil de usuario no encontrado." };
        
        const newComment = {
            id: doc(collection(clientDb, 'dummy')).id,
            userId: user.uid,
            username: userDoc.data().nickname,
            profileImageUrl: userDoc.data().profileImageUrl,
            text: commentText,
            createdAt: new Date().toISOString(),
        };

        const postRef = doc(clientDb, 'social_posts', postId);
        await updateDoc(postRef, { comments: arrayUnion(newComment) });
        return { success: true, message: "Comentario añadido." };
    } catch (e: any) {
        return { success: false, message: `Error al comentar: ${e.message}` };
    }
};
export const deleteCommentFromPost = async (postId: string, commentId: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, error: "No autenticado." };

    try {
        const postRef = doc(clientDb, 'social_posts', postId);
        const postSnap = await getDoc(postRef);

        if (postSnap.exists()) {
            const postData = postSnap.data() as SocialPost;
            const commentToDelete = postData.comments?.find(c => c.id === commentId);

            if (commentToDelete) {
                await updateDoc(postRef, { comments: arrayRemove(commentToDelete) });
                return { success: true, message: "Comentario eliminado." };
            }
        }
        return { success: false, error: "No se encontró el comentario." };
    } catch (e: any) {
        return { success: false, error: "Error al eliminar el comentario." };
    }
};
export const sharePostToStory = async (formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };

    const postId = formData.get('postId') as string;
    const authorCompositeId = formData.get('authorId') as string;
    if (!authorCompositeId) return { success: false, message: "Debes seleccionar un perfil para compartir."};

    const [authorType, authorId] = authorCompositeId.split(':') as ['hermandad' | 'banda' | 'organizacion' | 'empresa', string];

    try {
        const collectionName = authorType === 'hermandad' ? 'jartinet_hermandades' : `${authorType}s`;
        const entityRef = doc(clientDb, collectionName, authorId);
        const entityData = await checkAdminOrManager(user.uid, entityRef, ['hermanosMayores', 'directores', 'gestores']);

        // Si la publicación compartida tiene música, la historia hereda esa
        // canción para que suene mientras se ve.
        let music: Record<string, any> = {};
        try {
            const postSnap = await getDoc(doc(clientDb, 'social_posts', postId));
            if (postSnap.exists()) {
                const p = postSnap.data();
                music = {
                    spotifyUrl: p.spotifyUrl || undefined,
                    spotifyTrackName: p.spotifyTrackName || undefined,
                    spotifyStartTime: typeof p.spotifyStartTime === 'number' ? p.spotifyStartTime : undefined,
                    spotifyEndTime: typeof p.spotifyEndTime === 'number' ? p.spotifyEndTime : undefined,
                    audioPreviewUrl: p.audioPreviewUrl || undefined,
                };
            }
        } catch { /* sin música si falla */ }

        const storyData = {
            authorType,
            authorId,
            authorName: entityData.nombreCompleto || (entityData as JartinetHermandad).nombre,
            authorShieldUrl: entityData.escudoUrl,
            mediaUrl: '',
            mediaType: 'image',
            text: '',
            ...music,
            createdAt: serverTimestamp(),
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
            viewedBy: [user.uid],
            linkedPostId: postId,
        };
        Object.keys(storyData).forEach(k => (storyData as any)[k] === undefined && delete (storyData as any)[k]);
        await addDoc(collection(clientDb, 'social_stories'), storyData);
        return { success: true, message: "Publicación compartida en tu historia." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const getAllBandas = async () => { 
  try {
    const snapshot = await getDocs(collection(clientDb, 'bandas'));
    const bandas = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as Banda[];
    return { bandas };
  } catch (error: any) {
    console.error("Error fetching bandas:", error);
    return { bandas: [], error: `No se pudieron cargar las bandas: ${error.message}` };
  }
};

export const getAllUserProfiles = async () => {
    try {
        const snapshot = await getDocs(query(collection(clientDb, 'users')));
        const profiles = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as UserProfile[];
        return { profiles };
    } catch (e: any) {
        return { profiles: [], error: e.message };
    }
};
export const getAllSocialPosts = async () => {
    try {
        const snapshot = await getDocs(query(collection(clientDb, 'social_posts'), orderBy('createdAt', 'desc')));
        const posts = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as SocialPost[];
        return { posts };
    } catch (e: any) {
        return { posts: [], error: e.message };
    }
};
export const getAllActiveStories = async () => {
    try {
        const snapshot = await getDocs(query(collection(clientDb, 'social_stories'), where('expiresAt', '>', new Date())));
        const stories = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as SocialStory[];
        return { stories };
    } catch (e: any) {
        return { stories: [], error: e.message };
    }
};

export const getArchivedStories = async (authorId: string) => {
    try {
        const snapshot = await getDocs(query(collection(clientDb, 'social_stories'), where('authorId', '==', authorId)));
        const stories = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as SocialStory[];
        return { stories: stories.sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) };
    } catch (e: any) {
        console.error("Error fetching archived stories:", e);
        return { stories: [], error: e.message };
    }
};

export async function getStoriesByIds(storyIds: string[]): Promise<{ stories: SocialStory[], error?: string }> {
    if (storyIds.length === 0) {
        return { stories: [] };
    }
    try {
        const storiesRef = collection(clientDb, 'social_stories');
        const q = query(storiesRef, where(documentId(), 'in', storyIds));
        const snapshot = await getDocs(q);
        const stories = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as SocialStory[];
        const sortedStories = stories.sort((a, b) => storyIds.indexOf(a.id) - storyIds.indexOf(b.id));
        return { stories: sortedStories };
    } catch (e: any) {
        console.error("Error fetching stories by IDs:", e);
        return { stories: [], error: e.message };
    }
}


export const createHighlightStoryGroup = async (authorId: string, authorType: 'hermandad' | 'banda' | 'empresa', formData: FormData, storyIds: string[]) => {
    const title = formData.get('title') as string;
    const coverUrl = formData.get('coverUrl') as string;

    if (!title || !coverUrl || storyIds.length === 0) {
        return { success: false, message: "Título, portada e historias son requeridos." };
    }
    
    const newGroup: HighlightStoryGroup = {
        id: uuidv4(),
        name: title,
        coverUrl,
        storyIds,
        createdAt: new Date(),
    };
    
    const collectionName = authorType === 'hermandad' ? 'jartinet_hermandades' : `${authorType}s`;
    const entityRef = doc(clientDb, collectionName, authorId);
    
    try {
        await updateDoc(entityRef, { highlightGroups: arrayUnion(newGroup) });
        return { success: true, message: 'Grupo de destacadas creado.' };
    } catch(e: any) {
        return { success: false, message: `Error al crear el grupo: ${e.message}`};
    }
};

export const deleteHighlightStoryGroup = async (authorId: string, authorType: 'banda' | 'hermandad' | 'empresa', highlightId: string) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };

    try {
        const collectionName = authorType === 'hermandad' ? 'jartinet_hermandades' : `${authorType}s`;
        const entityRef = doc(clientDb, collectionName, authorId);
        const entitySnap = await getDoc(entityRef);
        if (!entitySnap.exists()) return { success: false, message: "La entidad no existe." };

        const entityData = entitySnap.data() as (Banda | JartinetHermandad | Empresa);
        const groupToDelete = entityData.highlightGroups?.find(g => g.id === highlightId);

        if (!groupToDelete) return { success: false, message: "El grupo de destacadas no existe." };
        
        await updateDoc(entityRef, { highlightGroups: arrayRemove(groupToDelete) });
        return { success: true, message: "Grupo de destacadas eliminado." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const deleteFeaturedStory = async (authorId: string, authorType: string, featuredStoryId: string) => {
    try {
        const featuredStoryRef = doc(clientDb, `social_profiles/${authorType}:${authorId}/featured_stories`, featuredStoryId);
        await deleteDoc(featuredStoryRef);
        return { success: true, message: "Historia destacada eliminada." };
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};

export const getAllOrganizaciones = async () => { 
  try {
    const snapshot = await getDocs(collection(clientDb, 'organizaciones'));
    const organizaciones = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as Organizacion[];
    return { organizaciones };
  } catch (error: any) {
    console.error("Error fetching organizaciones:", error);
    return { organizaciones: [], error: `No se pudieron cargar las organizaciones: ${error.message}` };
  }
};


export const createBandaSocialPost = async (bandaId: string, initialState: any, formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };

    try {
        const bandaRef = doc(clientDb, 'bandas', bandaId);
        const entityData = await checkAdminOrManager(user.uid, bandaRef, ['directores']);

        const postData = {
            authorType: 'banda' as const,
            authorId: bandaId,
            authorName: entityData.nombreCompleto,
            authorShieldUrl: entityData.escudoUrl,
            creatorId: user.uid,
            title: formData.get('title') as string,
            content: formData.get('content') as string,
            mediaUrl: Array.from({ length: 10 }, (_, i) => formData.get(`mediaUrl-${i + 1}`) as string).filter(Boolean),
            mediaType: 'image' as const,
            spotifyUrl: formData.get('spotifyUrl') as string || undefined,
            spotifyStartTime: formData.get('spotifyStartTime') ? parseInt(formData.get('spotifyStartTime') as string) : undefined,
            spotifyEndTime: formData.get('spotifyEndTime') ? parseInt(formData.get('spotifyEndTime') as string) : undefined,
            spotifyTrackName: formData.get('spotifyTrackName') as string || undefined,
            audioPreviewUrl: formData.get('audioPreviewUrl') as string || undefined,
            createdAt: serverTimestamp(),
            likes: [],
            comments: [],
            collaboratorId: formData.get('collaboratorId') as string === 'none' ? null : formData.get('collaboratorId') as string,
            collaborationStatus: formData.get('collaboratorId') as string === 'none' ? null : 'pending' as const,
        };

        if(postData.collaboratorId) {
            const [type, id] = postData.collaboratorId.split(':');
            const collectionName = type === 'hermandad' ? 'jartinet_hermandades' : `${type}s`;
            const collaboratorSnap = await getDoc(doc(clientDb, collectionName, id));
            if(collaboratorSnap.exists()){
                const collaboratorData = collaboratorSnap.data();
                postData.collaboratorName = collaboratorData.nombreCompleto || collaboratorData.nombre;
                postData.collaboratorShieldUrl = collaboratorData.escudoUrl;
            }
        }

        Object.keys(postData).forEach(k => (postData as any)[k] === undefined && delete (postData as any)[k]);
        await addDoc(collection(clientDb, 'social_posts'), postData);
        return { success: true, message: "Publicación creada con éxito." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};
export const createBandaSocialStory = async (bandaId: string, initialState: any, formData: FormData) => {
    return createSocialStory(bandaId, 'banda', formData);
};

export const createOrganizacionSocialPost = async (initialState: any, formData: FormData) => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: "No autenticado." };

    try {
        const organizacionId = formData.get('organizacionId') as string;
        if (!organizacionId) return { success: false, message: "Debe seleccionar una organización." };
        const orgRef = doc(clientDb, 'organizaciones', organizacionId);
        const entityData = await checkAdminOrManager(user.uid, orgRef, ['gestores']);

        const postData = {
            authorType: 'organizacion' as const,
            authorId: organizacionId,
            authorName: entityData.nombreCompleto,
            authorShieldUrl: entityData.escudoUrl,
            creatorId: user.uid,
            title: formData.get('title') as string,
            content: formData.get('content') as string,
            mediaUrl: Array.from({ length: 10 }, (_, i) => formData.get(`mediaUrl-${i + 1}`) as string).filter(Boolean),
            mediaType: 'image' as const,
            spotifyUrl: formData.get('spotifyUrl') as string || undefined,
            spotifyStartTime: formData.get('spotifyStartTime') ? parseInt(formData.get('spotifyStartTime') as string) : undefined,
            spotifyEndTime: formData.get('spotifyEndTime') ? parseInt(formData.get('spotifyEndTime') as string) : undefined,
            spotifyTrackName: formData.get('spotifyTrackName') as string || undefined,
            audioPreviewUrl: formData.get('audioPreviewUrl') as string || undefined,
            createdAt: serverTimestamp(),
            likes: [],
            comments: [],
            collaboratorId: formData.get('collaboratorId') as string === 'none' ? null : formData.get('collaboratorId') as string,
            collaborationStatus: formData.get('collaboratorId') as string === 'none' ? null : 'pending' as const,
        };

        if(postData.collaboratorId) {
            const [type, id] = postData.collaboratorId.split(':');
            const collectionName = type === 'hermandad' ? 'jartinet_hermandades' : `${type}s`;
            const collaboratorSnap = await getDoc(doc(clientDb, collectionName, id));
            if(collaboratorSnap.exists()){
                const collaboratorData = collaboratorSnap.data();
                postData.collaboratorName = collaboratorData.nombreCompleto || collaboratorData.nombre;
                postData.collaboratorShieldUrl = collaboratorData.escudoUrl;
            }
        }

        Object.keys(postData).forEach(k => (postData as any)[k] === undefined && delete (postData as any)[k]);
        await addDoc(collection(clientDb, 'social_posts'), postData);
        return { success: true, message: "Publicación creada con éxito." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};
export const deleteContract = async (contractId: string) => {
    try {
        await deleteDoc(doc(clientDb, 'contracts', contractId));
        return { success: true, message: "Contrato eliminado." };
    } catch (e: any) {
        return { success: false, error: "No se pudo eliminar el contrato." };
    }
};

export const login = async (uid: string) => { return { success: false, error: "Not implemented" } };

export const getAllAvisos = async () => {
  try {
    const q = query(collection(clientDb, 'avisos'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const avisos = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as Aviso[];
    return { avisos };
  } catch (error: any) {
    return { avisos: [], error: `No se pudieron cargar los avisos: ${error.message}` };
  }
};

export async function getAllNoticias() {
    try {
        const q = query(collection(clientDb, "noticias"), orderBy("date", "desc"));
        const snapshot = await getDocs(q);
        const noticias = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as Noticia[];
        return { noticias };
    } catch (e: any) {
        return { noticias: [], error: e.message };
    }
}


export async function getAllPeriodicoArticles() {
  try {
    const q = query(collection(clientDb, 'periodico_articles'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const articles = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as PeriodicoArticle[];
    return { articles };
  } catch (error: any) {
    return { articles: [], error: `No se pudieron cargar los artículos: ${error.message}` };
  }
}

const EmpresaSchema = z.object({
  nombreCompleto: z.string().min(3, "El nombre es requerido."),
  escudoUrl: z.string().url("La URL del logo no es válida."),
  bio: z.string().optional(),
  colorPrimario: z.string().startsWith('#', "Debe ser un color hexadecimal."),
  colorSecundario: z.string().startsWith('#', "Debe ser un color hexadecimal."),
  gestores: z.string().transform(val => val.split(',').map(s => s.trim()).filter(Boolean)),
});

export async function getAllEmpresas() {
    try {
        const q = query(collection(clientDb, 'empresas'), orderBy('nombreCompleto', 'asc'));
        const snapshot = await getDocs(q);
        const empresas = snapshot.docs.map(doc => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as Empresa[];
        return { empresas };
    } catch (error: any) {
        return { empresas: [], error: `No se pudieron cargar las empresas: ${error.message}` };
    }
}

export async function createEmpresa(formData: FormData) {
  const validatedFields = EmpresaSchema.safeParse(Object.fromEntries(formData.entries()));

  if (!validatedFields.success) {
    return { success: false, message: "Datos inválidos.", errors: validatedFields.error.flatten().fieldErrors };
  }

  try {
    await addDoc(collection(clientDb, 'empresas'), { ...validatedFields.data });
    return { success: true, message: 'Empresa creada con éxito.' };
  } catch (e: any) {
    return { success: false, message: `Error al crear la empresa: ${e.message}` };
  }
}

export async function updateEmpresa(id: string, formData: FormData) {
  const validatedFields = EmpresaSchema.safeParse(Object.fromEntries(formData.entries()));

  if (!validatedFields.success) {
    return { success: false, message: "Datos inválidos.", errors: validatedFields.error.flatten().fieldErrors };
  }

  try {
    await updateDoc(doc(clientDb, 'empresas', id), { ...validatedFields.data });
    return { success: true, message: 'Empresa actualizada con éxito.' };
  } catch (e: any) {
    return { success: false, message: `Error al actualizar la empresa: ${e.message}` };
  }
}

export async function deleteEmpresa(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'empresas', id));
        return { success: true, message: 'Empresa eliminada con éxito.' };
    } catch (e: any) {
        return { success: false, error: `No se pudo eliminar la empresa: ${e.message}` };
    }
}

export const saveHermandadSettings = async (hermandadId: string, updates: Partial<JartinetHermandad>) => {
  const updateHermandadSettingsFn = httpsCallable(functions, 'updateHermandadSettings');
  try {
    const result = await updateHermandadSettingsFn({ hermandadId, updates });
    return result.data as { success: boolean, message: string };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
}

export const adminCreateHermandad = async (data: Partial<JartinetHermandad>) => {
  const fn = httpsCallable(functions, 'adminCreateHermandad');
  try {
    const result = await fn({ data });
    return result.data as { success: boolean, message: string, id?: string };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
};

export const adminDeleteHermandad = async (hermandadId: string) => {
  const fn = httpsCallable(functions, 'adminDeleteHermandad');
  try {
    const result = await fn({ hermandadId });
    return result.data as { success: boolean, message: string };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
};

export const saveBandaSettings = async (bandaId: string, updates: Partial<Banda>) => {
  const updateBandaSettingsFn = httpsCallable(functions, 'updateBandaSettings');
  try {
    const result = await updateBandaSettingsFn({ bandaId, updates });
    return result.data as { success: boolean, message: string };
  } catch(e: any) {
    return { success: false, message: e.message };
  }
}

export const adminCreateBanda = async (data: Partial<Banda>) => {
    const fn = httpsCallable(functions, 'adminCreateBanda');
    try {
        const result = await fn({ data });
        return result.data as { success: boolean; message: string; id?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminDeleteBanda = async (bandaId: string) => {
    const fn = httpsCallable(functions, 'adminDeleteBanda');
    try {
        const result = await fn({ bandaId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const createOrganizacion = async (data: Partial<Organizacion>) => {
    const fn = httpsCallable(functions, 'createOrganizacion');
    try {
        const result = await fn({ data });
        return result.data as { success: boolean, message: string, id?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const saveOrganizacionSettings = async (orgId: string, updates: Partial<Organizacion>) => {
  const updateOrganizacionSettingsFn = httpsCallable(functions, 'updateOrganizacionSettings');
  try {
    const result = await updateOrganizacionSettingsFn({ orgId, updates });
    return result.data as { success: boolean, message: string };
  } catch(e: any) {
    return { success: false, message: e.message };
  }
}

export const adminDeleteOrganizacion = async (orgId: string) => {
  const fn = httpsCallable(functions, 'adminDeleteOrganizacion');
  try {
    const result = await fn({ orgId });
    return result.data as { success: boolean; message: string };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
};

export async function createDiaSS(formData: FormData) {
    const createDiaSSFn = httpsCallable(functions, 'createDiaSS');
    try {
        const data = {
            nombredia: formData.get('nombredia'),
            tipo: formData.get('tipo'),
            orden: Number(formData.get('orden')),
            fecha: formData.get('fecha') as string, // La función se encargará de esto
            hermandades: formData.getAll('hermandades'),
        };
        const result = await createDiaSSFn(data);
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
}

export async function getDiasSS() {
    const getDiasSSFn = httpsCallable(functions, 'getDiasSS');
    try {
        const result = await getDiasSSFn();
        const data = result.data as { dias: DiaSS[], error?: string };
        if(data.error) throw new Error(data.error);
        return { dias: data.dias || [] };
    } catch (e: any) {
        return { dias: [], error: e.message };
    }
}

export const sendCarta = async (hermandadId: string, formData: FormData) => {
    const sendCartaFn = httpsCallable(functions, 'sendCarta');
    try {
        const data = {
            hermandadId,
            subject: formData.get('subject') as string,
            content: formData.get('content') as string,
        };
        const result = await sendCartaFn(data);
        return result.data as { success: boolean, message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const sendArzobispadoCarta = async (subject: string, content: string) => {
    const fn = httpsCallable(functions, 'sendArzobispadoCarta');
    try {
        const result = await fn({ subject, content });
        return result.data as { success: boolean, message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

// Hermano Mayor / admin: envía un anuncio al servidor principal de Minecraft
// (se muestra como bossbar durante 1 minuto a todos los jugadores).
// `titulo` (opcional, SOLO admin) sustituye el nombre de la hermandad en la
// barra: es lo que permite que el CECOP mande avisos firmados "CECOP".
export const sendServerAnnouncement = async (hermandadId: string, message: string, titulo?: string) => {
    const fn = httpsCallable(functions, 'sendServerAnnouncement');
    try {
        const result = await fn({ hermandadId, message, titulo });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const getCartas = async () => {
    const getCartasFn = httpsCallable(functions, 'getCartas');
    try {
        const result = await getCartasFn();
        return result.data as { success: boolean, cartas?: Carta[], error?: string };
    } catch (e: any) {
        return { success: false, error: e.message, cartas: [] };
    }
};


export const createBankAccount = async (accountName: string, pin: string) => {
    const createBankAccountFn = httpsCallable(functions, 'createBankAccount');
    try {
        const result = await createBankAccountFn({ accountName, pin });
        return result.data as { success: boolean, message: string, accountId?: string };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const generateMcLinkCode = async () => {
    const fn = httpsCallable(functions, 'generateMcLinkCode');
    try {
        const result = await fn({});
        return result.data as { success: boolean, code?: string, message?: string };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const createPrincipalBankAccount = async (accountName: string, pin: string) => {
    const fn = httpsCallable(functions, 'createPrincipalBankAccount');
    try {
        const result = await fn({ accountName, pin });
        return result.data as { success: boolean, message: string, accountId?: string };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const searchUsersByNickname = async (query: string) => {
    const fn = httpsCallable(functions, 'searchUsersByNickname');
    try {
        const result = await fn({ query });
        return result.data as { users: { id: string; nickname: string; profileImageUrl: string | null }[] };
    } catch (error: any) {
        return { users: [] };
    }
};

export const updateBankAccountSharing = async (accountId: string, targetUserId: string, action: 'add' | 'remove') => {
    const fn = httpsCallable(functions, 'updateBankAccountSharing');
    try {
        const result = await fn({ accountId, targetUserId, action });
        return result.data as { success: boolean, message: string };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const getMyBankAccounts = async () => {
    const fn = httpsCallable(functions, 'getMyBankAccounts');
    try {
        const result = await fn({});
        return result.data as { success: boolean, accounts?: BankAccount[], message?: string };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const getBankData = async (accountName: string, pin: string) => {
    const getBankDataFn = httpsCallable(functions, 'getBankData');
    try {
        const result = await getBankDataFn({ accountName, pin });
        return result.data as { success: boolean, message?: string, account?: BankAccount };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const getTransactions = async (iban: string) => {
    const getTransactionsFn = httpsCallable(functions, 'getTransactions');
    try {
        const result = await getTransactionsFn({ iban });
        return result.data as { success: boolean, message?: string, transactions?: Transaction[] };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const getAccountByIban = async (iban: string) => {
    const getAccountByIbanFn = httpsCallable(functions, 'getAccountByIban');
    try {
        const result = await getAccountByIbanFn({ iban });
        return result.data as { success: boolean, message?: string, account?: { iban: string, accountName: string } };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
}

export const makeTransfer = async (fromIban: string, toIban: string, amount: number, concept: string, currency: 'oro' | 'plata' | 'bronce' = 'oro') => {
    const makeTransferFn = httpsCallable(functions, 'makeTransfer');
    try {
        const result = await makeTransferFn({ fromIban, toIban, amount, concept, currency });
        return result.data as { success: boolean, message?: string };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
}

export const updateDiaSSHermandad = async (diaId: string, hermandadId: string, detalles: any) => {
    const updateFn = httpsCallable(functions, 'updateDiaSSHermandad');
    try {
        const result = await updateFn({ diaId, hermandadId, detalles });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

// --- PROGRAMA SEMANA SANTA: LIBRO ---

export async function getProgramaSSConfig() {
    try {
        const snap = await getDoc(doc(clientDb, 'programa_ss_config', 'main'));
        const config = snap.exists() ? (sanitizeFirestoreData({ id: snap.id, ...snap.data() }) as ProgramaSSConfig) : null;
        return { config };
    } catch (error: any) {
        return { config: null, error: `No se pudo cargar la configuración del libro: ${error.message}` };
    }
}

export async function setProgramaSSPortadaLibro(file: File) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const storageRef = ref(storage, `programa_ss/portada_libro_${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file, CACHE_SUBIDAS);
        const url = await getDownloadURL(storageRef);
        await setDoc(doc(clientDb, 'programa_ss_config', 'main'), { portadaLibroUrl: url, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Portada del libro actualizada.', url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la portada: ${e.message}` };
    }
}

export async function setCarreraOficial(puntos: { x: number; y: number }[]) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'programa_ss_config', 'main'), { carreraOficial: puntos, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Carrera Oficial guardada.' };
    } catch (e: any) {
        return { success: false, message: `Error al guardar la Carrera Oficial: ${e.message}` };
    }
}

/**
 * Copia la calibración del mapa del servidor (`mod_map_config/main`, solo-admin)
 * dentro de la configuración del programa, que es de lectura pública. Es lo que
 * permite dar en kilómetros la distancia de cada recorrido en el libro, que
 * también se ve sin sesión.
 *
 * OJO: si se recalibra el mapa del servidor hay que volver a copiarla (el panel
 * de admin del programa lo hace solo al abrirse).
 */
export async function syncProgramaSSMapCalib() {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const snap = await getDoc(doc(clientDb, 'mod_map_config', 'main'));
        const calib = snap.exists() ? (snap.data() as any).calib : null;
        if (!Array.isArray(calib) || calib.length < 2) {
            return { success: false, message: 'El mapa del servidor todavía no está calibrado.' };
        }
        // Solo los cuatro campos que hacen falta, y nunca `undefined`.
        const limpia = calib.slice(0, 2).map((c: any) => ({
            xPct: Number(c?.xPct) || 0,
            yPct: Number(c?.yPct) || 0,
            mcX: Number(c?.mcX) || 0,
            mcZ: Number(c?.mcZ) || 0,
        }));
        await setDoc(doc(clientDb, 'programa_ss_config', 'main'), { mapCalib: limpia, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Escala del mapa actualizada.', calib: limpia };
    } catch (e: any) {
        return { success: false, message: `No se pudo copiar la calibración: ${e.message}` };
    }
}

export async function setCatedralPunto(punto: { x: number; y: number }) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'programa_ss_config', 'main'), { catedralPunto: punto, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Punto de la Catedral guardado.' };
    } catch (e: any) {
        return { success: false, message: `Error al guardar el punto de la Catedral: ${e.message}` };
    }
}

export async function setDiaPortada(diaId: string, file: File) {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const storageRef = ref(storage, `programa_ss/dias/${diaId}_${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file, CACHE_SUBIDAS);
        const url = await getDownloadURL(storageRef);
        await updateDoc(doc(clientDb, 'dias_ss', diaId), { portadaUrl: url });
        return { success: true, message: 'Portada del día actualizada.', url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la portada del día: ${e.message}` };
    }
}

export async function removeDiaPortada(diaId: string) {
    try {
        await updateDoc(doc(clientDb, 'dias_ss', diaId), { portadaUrl: '' });
        return { success: true, message: 'Portada eliminada.' };
    } catch (e: any) {
        return { success: false, message: `Error al eliminar la portada: ${e.message}` };
    }
}

export const submitRecorrido = async (
    diaId: string,
    hermandadId: string,
    data: {
        iglesia: { x: number; y: number };
        ida: { x: number; y: number }[];
        vuelta: { x: number; y: number }[];
        /** Solo en días de vísperas: iglesia donde se hace estación de penitencia. */
        estacion?: { x: number; y: number } | null;
    }
) => {
    const submitFn = httpsCallable(functions, 'submitRecorrido');
    try {
        const result = await submitFn({ diaId, hermandadId, ...data, estacion: data.estacion ?? null });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

/** El hermano mayor manda el cuadro de horarios de su hermandad para ese día. */
export const submitHorario = async (
    diaId: string,
    hermandadId: string,
    itinerario: Record<string, { calle: string; tipo: string }>,
) => {
    const fn = httpsCallable(functions, 'submitHorario');
    try {
        const result = await fn({ diaId, hermandadId, itinerario });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const reviewHorario = async (diaId: string, hermandadId: string, accion: 'aprobado' | 'rechazado', motivo?: string) => {
    const fn = httpsCallable(functions, 'reviewHorario');
    try {
        const result = await fn({ diaId, hermandadId, accion, motivo });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

/**
 * Borra lo enviado por una hermandad en un día: el recorrido, el cuadro de
 * horarios (que se lleva el itinerario, porque es el propio cuadro) o ambos.
 */
export const adminDeleteSolicitud = async (diaId: string, hermandadId: string, tipo: 'recorrido' | 'horario' | 'ambos') => {
    const fn = httpsCallable(functions, 'admin_deleteSolicitud');
    try {
        const result = await fn({ diaId, hermandadId, tipo });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

/** Borra todos los cuadros de horarios (o los de un día concreto). Solo admin. */
export const adminClearHorarios = async (diaId?: string) => {
    const fn = httpsCallable(functions, 'admin_clearHorarios');
    try {
        const result = await fn(diaId ? { diaId } : {});
        return result.data as { success: boolean; borrados: number; message: string };
    } catch (e: any) {
        return { success: false, borrados: 0, message: e.message };
    }
};

export async function uploadProgramaSSImage(file: File, path: string): Promise<string> {
    const listo = await paraSubir(file);
    const ruta = listo === file ? path : path.replace(/\.[^./]*$/, '') + '.webp';
    const storageRef = ref(storage, `programa_ss/${ruta}`);
    await uploadBytes(storageRef, listo, CACHE_SUBIDAS);
    return getDownloadURL(storageRef);
}

/** Entrada de un ranking del Hall of Fame. */
export interface HallOfFameEntry {
    id: string;
    nombre: string;
    escudoUrl?: string;
    uuid?: string;
    colorPrimario?: string;
    valor: number;
}

export interface HallOfFameData {
    /** Hermandades, bandas y organizaciones con más me gustas en la red social. */
    perfilesPorMeGusta: HallOfFameEntry[];
    /** Suma del oro de todas las cuentas a las que cada usuario tiene acceso. */
    fortunas: HallOfFameEntry[];
    hermandadesPorHermanos: HallOfFameEntry[];
    hermandadesMasAntiguas: HallOfFameEntry[];
    bandasPorContratos: HallOfFameEntry[];
    bandasPorRepertorio: HallOfFameEntry[];
    jugadoresPorHoras: HallOfFameEntry[];
    totales: { hermandades: number; bandas: number; contratos: number; hermanos: number };
}

// ── Carnet de hermano ────────────────────────────────────────────────────────

export interface CarnetEntidad {
    id: string;
    nombre: string;
    nombreCompleto?: string;
    escudoUrl?: string;
    colorPrimario?: string;
    tipo?: string;
    dia?: string;
    esHermanoMayor?: boolean;
}

export interface CarnetProcesion {
    id: string;
    hermandadId: string;
    hermandadNombre: string;
    hermandadEscudo: string;
    dia: string;
    cargo: string;
    hora: string;
    sentAt: string | null;
}

export interface CarnetData {
    perfil: {
        id: string;
        nickname: string;
        profileImageUrl: string;
        bio: string;
        role: 'user' | 'admin';
        mcUuid: string;
        minecraftId: string;
        desde: string | null;
        /** Solo llega si el carnet es tuyo o eres admin; si no, `null`. */
        email: string | null;
        discordId: string | null;
    };
    esPropio: boolean;
    hermandades: CarnetEntidad[];
    bandas: CarnetEntidad[];
    organizaciones: CarnetEntidad[];
    procesiones: CarnetProcesion[];
    cromos: { distintos: number; copias: number; total: number };
    juego: { horas: number; ultimaVez: string | null; vinculado: boolean };
}

/**
 * Ficha de un ciudadano. Va por Cloud Function porque casi nada de lo que
 * enseña es legible desde el cliente (perfil, papeletas y actividad del mod).
 * Sin `uid` devuelve el carnet propio.
 */
export const getCarnet = async (uid?: string): Promise<{ carnet: CarnetData | null; error?: string }> => {
    const fn = httpsCallable(functions, 'getCarnet');
    try {
        const result = await fn(uid ? { uid } : {});
        return { carnet: result.data as CarnetData };
    } catch (error: any) {
        return { carnet: null, error: error.message };
    }
};

/** Rankings públicos (página /hall-of-fame). Se calculan en Cloud Functions. */
export const getHallOfFame = async (): Promise<HallOfFameData | null> => {
    const fn = httpsCallable(functions, 'getHallOfFame');
    try {
        const result = await fn({});
        return result.data as HallOfFameData;
    } catch {
        return null;
    }
};

/**
 * Asigna (o quita) el día del programa de Semana Santa de una hermandad.
 * Mantiene en sync `jartinet_hermandades.dia` y su puesto en `dias_ss`.
 */
export const adminSetHermandadDiaSS = async (hermandadId: string, diaId: string | null) => {
    const fn = httpsCallable(functions, 'admin_setHermandadDiaSS');
    try {
        const result = await fn({ hermandadId, diaId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

/** Actualiza los metadatos de un día del programa (nombre, tipo, orden, fecha). */
export const adminUpdateDiaSS = async (diaId: string, updates: Record<string, any>) => {
    const fn = httpsCallable(functions, 'updateDiaSS');
    try {
        const result = await fn({ diaId, updates });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

/** Borra todos los recorridos enviados (o los de un día concreto). Solo admin. */
export const adminClearRecorridos = async (diaId?: string) => {
    const fn = httpsCallable(functions, 'admin_clearRecorridos');
    try {
        const result = await fn(diaId ? { diaId } : {});
        return result.data as { success: boolean; borrados: number; message: string };
    } catch (e: any) {
        return { success: false, borrados: 0, message: e.message };
    }
};

export const reviewRecorrido = async (diaId: string, hermandadId: string, accion: 'aprobado' | 'rechazado', color?: string) => {
    const reviewFn = httpsCallable(functions, 'reviewRecorrido');
    try {
        const result = await reviewFn({ diaId, hermandadId, accion, color });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const sendPapeleta = async (hermandadId: string, papeletaData: any) => {
    const sendPapeletaFn = httpsCallable(functions, 'sendPapeleta');
    try {
        const result = await sendPapeletaFn({ hermandadId, papeletaData });
        return result.data as { success: boolean; message: string; };
    } catch (error: any) {
        return { success: false, message: error.message };
    }
};

export const getPapeletasByHermandad = async (hermandadId: string) => {
    const getPapeletasFn = httpsCallable(functions, 'getPapeletasByHermandad');
    try {
        const result = await getPapeletasFn({ hermandadId });
        return result.data as { success: boolean, papeletas?: PapeletaDeSitio[], error?: string };
    } catch (e: any) {
        return { success: false, error: e.message };
    }
};

export const deletePapeleta = async (hermandadId: string, papeletaId: string) => {
    const fn = httpsCallable(functions, 'deletePapeleta');
    try {
        const result = await fn({ hermandadId, papeletaId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const createFixedTermDeposit = async (accountId: string, amount: number, termDays: number) => {
    const fn = httpsCallable(functions, 'createFixedTermDeposit');
    try {
        const result = await fn({ accountId, amount, termDays });
        return result.data as { success: boolean, message?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const getMyInvestments = async (accountId: string) => {
    const fn = httpsCallable(functions, 'getMyInvestments');
    try {
        const result = await fn({ accountId });
        return result.data as { success: boolean, deposits: FixedTermDeposit[], investments: JartidexInvestment[], message?: string };
    } catch (e: any) {
        return { success: false, message: e.message, deposits: [], investments: [] };
    }
};

export const admin_getAllInvestments = async () => {
    const fn = httpsCallable(functions, 'admin_getAllInvestments');
    try {
        const result = await fn();
        return result.data as { success: boolean, deposits: FixedTermDeposit[], investments: JartidexInvestment[], message?: string };
    } catch(e: any) {
        return { success: false, message: e.message, deposits: [], investments: [] };
    }
};

export const admin_updateBankSettings = async (updates: Partial<BankSettings>) => {
    const fn = httpsCallable(functions, 'admin_updateBankSettings');
    try {
        const result = await fn({ updates });
        return result.data as { success: boolean, message?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const admin_updateJartidex = async (data: { changePercent: number, eventDescription?: string }) => {
    const fn = httpsCallable(functions, 'admin_updateJartidex');
    try {
        const result = await fn(data);
        return result.data as { success: boolean, message?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const getJartidexHistory = async () => {
    const fn = httpsCallable(functions, 'getJartidexHistory');
    try {
        const result = await fn();
        return result.data as { history: JartidexHistory[], error?: string };
    } catch(e: any) {
        return { history: [], error: e.message };
    }
};

export const getBankSettings = async () => {
    const fn = httpsCallable(functions, 'getBankSettings');
    try {
        const result = await fn();
        return result.data as { settings: BankSettings, error?: string };
    } catch(e: any) {
        return { settings: null, error: e.message };
    }
};

export const createJartidexInvestment = async (accountId: string, amount: number) => {
    const fn = httpsCallable(functions, 'createJartidexInvestment');
    try {
        const result = await fn({ accountId, amount });
        return result.data as { success: boolean, message?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const sellJartidexInvestment = async (accountId: string, sharesToSell: number) => {
    const fn = httpsCallable(functions, 'sellJartidexInvestment');
    try {
        const result = await fn({ accountId, sharesToSell });
        return result.data as { success: boolean, message?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const createBono = async (accountId: string, quantity: number, termDays: number) => {
    const fn = httpsCallable(functions, 'createBono');
    try {
        const result = await fn({ accountId, quantity, termDays });
        return result.data as { success: boolean, message?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const getMyBonos = async (accountId: string) => {
    const fn = httpsCallable(functions, 'getMyBonos');
    try {
        const result = await fn({ accountId });
        return result.data as { success: boolean, bonos: import('./types').BonoInversion[], message?: string };
    } catch (e: any) {
        return { success: false, bonos: [], message: e.message };
    }
};

export const redeemBono = async (bonoId: string, accountId: string) => {
    const fn = httpsCallable(functions, 'redeemBono');
    try {
        const result = await fn({ bonoId, accountId });
        return result.data as { success: boolean, message?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

// ─────────────────────────────────────────────────────────────────────────────
//  JARTICARDS
// ─────────────────────────────────────────────────────────────────────────────

interface JarticardInput {
    type: JarticardType;
    name: string;
    imageUrl: string;
    crestUrl?: string | null;
}

export const adminCreateJarticard = async (data: JarticardInput) => {
    const fn = httpsCallable(functions, 'adminCreateJarticard');
    try {
        const result = await fn(data);
        return result.data as { success: boolean; id?: string; number?: number; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminUpdateJarticard = async (cardId: string, updates: Partial<JarticardInput>) => {
    const fn = httpsCallable(functions, 'adminUpdateJarticard');
    try {
        const result = await fn({ cardId, updates });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminDeleteJarticard = async (cardId: string) => {
    const fn = httpsCallable(functions, 'adminDeleteJarticard');
    try {
        const result = await fn({ cardId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminSaveSobre = async (sobreId: string | null, data: Partial<JarticardSobre>) => {
    const fn = httpsCallable(functions, 'adminSaveSobre');
    try {
        const result = await fn({ sobreId, data });
        return result.data as { success: boolean; id?: string; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminDeleteSobre = async (sobreId: string) => {
    const fn = httpsCallable(functions, 'adminDeleteSobre');
    try {
        const result = await fn({ sobreId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminGetUserJarticards = async (targetUserId: string) => {
    const fn = httpsCallable(functions, 'adminGetUserJarticards');
    try {
        const result = await fn({ targetUserId });
        return result.data as { success: boolean; owned: UserJarticard[]; message?: string };
    } catch (e: any) {
        return { success: false, owned: [], message: e.message };
    }
};

export const adminGetTopCollections = async (limit = 30) => {
    const fn = httpsCallable(functions, 'adminGetTopCollections');
    try {
        const result = await fn({ limit });
        return result.data as {
            success: boolean;
            totalCards: number;
            ranking: { id: string; nickname: string; unique: number; copies: number; percent: number }[];
            message?: string;
        };
    } catch (e: any) {
        return { success: false, totalCards: 0, ranking: [], message: e.message };
    }
};

export const claimDailySobre = async (sobreId: string) => {
    const fn = httpsCallable(functions, 'claimDailySobre');
    try {
        const result = await fn({ sobreId });
        return result.data as { success: boolean; drawn?: string[]; message: string; nextAvailableAt?: number };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const getDailySobreStatus = async (sobreId: string) => {
    const fn = httpsCallable(functions, 'getDailySobreStatus');
    try {
        const result = await fn({ sobreId });
        return result.data as { success: boolean; available: boolean; nextAvailableAt: number };
    } catch (e: any) {
        return { success: false, available: false, nextAvailableAt: 0 };
    }
};

export const buyJarticardSobre = async (accountId: string, sobreId: string) => {
    const fn = httpsCallable(functions, 'buyJarticardSobre');
    try {
        const result = await fn({ accountId, sobreId });
        return result.data as { success: boolean; drawn?: string[]; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const createMarketListing = async (cardId: string) => {
    const fn = httpsCallable(functions, 'createMarketListing');
    try {
        const result = await fn({ cardId });
        return result.data as { success: boolean; id?: string; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const cancelMarketListing = async (listingId: string) => {
    const fn = httpsCallable(functions, 'cancelMarketListing');
    try {
        const result = await fn({ listingId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const makeMarketOffer = async (listingId: string, offeredCardId: string) => {
    const fn = httpsCallable(functions, 'makeMarketOffer');
    try {
        const result = await fn({ listingId, offeredCardId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const cancelMarketOffer = async (listingId: string, offerId: string) => {
    const fn = httpsCallable(functions, 'cancelMarketOffer');
    try {
        const result = await fn({ listingId, offerId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const acceptMarketOffer = async (listingId: string, offerId: string) => {
    const fn = httpsCallable(functions, 'acceptMarketOffer');
    try {
        const result = await fn({ listingId, offerId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const rejectMarketOffer = async (listingId: string, offerId: string) => {
    const fn = httpsCallable(functions, 'rejectMarketOffer');
    try {
        const result = await fn({ listingId, offerId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

// ── Gestión de usuarios (admin) ──
export const adminGetUserProfile = async (uid: string) => {
    const fn = httpsCallable(functions, 'adminGetUserProfile');
    try {
        const result = await fn({ uid });
        return result.data as { success: boolean; profile: UserProfile; auth: { email: string; disabled: boolean; emailVerified: boolean } | null; message?: string };
    } catch (e: any) {
        return { success: false, profile: null as any, auth: null, message: e.message };
    }
};

export const adminUpdateUserProfile = async (uid: string, updates: Partial<UserProfile> & { email?: string }) => {
    const fn = httpsCallable(functions, 'adminUpdateUserProfile');
    try {
        const result = await fn({ uid, updates });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminSetUserPassword = async (uid: string, newPassword: string) => {
    const fn = httpsCallable(functions, 'adminSetUserPassword');
    try {
        const result = await fn({ uid, newPassword });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminSetUserBanned = async (uid: string, banned: boolean, reason?: string) => {
    const fn = httpsCallable(functions, 'adminSetUserBanned');
    try {
        const result = await fn({ uid, banned, reason });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const adminDeleteUser = async (uid: string) => {
    const fn = httpsCallable(functions, 'adminDeleteUser');
    try {
        const result = await fn({ uid });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

// ── Subida de medios para la red social (archivo → Storage) ──
export async function uploadSocialMedia(file: File): Promise<{ success: boolean; url?: string; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const path = `social/${user.uid}/${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`;
        const url = await subirImagenConMiniatura(path, file);
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: `Error al subir: ${e.message}` };
    }
}

// ── Colores de evento por hermandad (app_settings/calendario) ──
// Lectura pública / escritura admin, igual que el resto de `app_settings`.
export async function getCalendarioHermandadColors(): Promise<Record<string, string>> {
    try {
        const snap = await getDoc(doc(clientDb, 'app_settings', 'calendario'));
        if (!snap.exists()) return {};
        return (snap.data()?.hermandadColors as Record<string, string>) || {};
    } catch {
        return {};
    }
}

export async function setCalendarioHermandadColors(hermandadColors: Record<string, string>) {
    try {
        await setDoc(doc(clientDb, 'app_settings', 'calendario'), { hermandadColors }, { merge: true });
        return { success: true, message: 'Colores guardados.' };
    } catch (e: any) {
        return { success: false, message: `No se pudieron guardar los colores: ${e.message}` };
    }
}

// ── Imagen de un artículo del periódico (archivo → Storage) ──
export async function uploadPeriodicoImage(file: File): Promise<{ success: boolean; url?: string; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const path = `periodico/${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`;
        const url = await subirImagenConMiniatura(path, file);
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la imagen: ${e.message}` };
    }
}

// ════════════════════════════════════════════════════════════════════
// ── TIENDA DE PERSONALIZABLES (packs_products / packs_orders) ──
// ════════════════════════════════════════════════════════════════════
// El catálogo lo escribe el admin directamente (reglas `isAdmin()`). Todo lo que
// toca pedidos, stock o el chat pasa por Cloud Function.

export async function uploadPackImage(file: File): Promise<{ success: boolean; url?: string; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const path = `packs/${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`;
        const url = await subirImagenConMiniatura(path, file);
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la imagen: ${e.message}` };
    }
}

/** Fotos de referencia que adjunta el comprador en su solicitud o en el chat. */
export async function uploadPackRequestImage(file: File): Promise<{ success: boolean; url?: string; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const path = `packs/solicitudes/${user.uid}/${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`;
        const url = await subirImagenConMiniatura(path, file);
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la imagen: ${e.message}` };
    }
}

export async function createPackProduct(data: Partial<PackProduct>) {
    try {
        await addDoc(collection(clientDb, 'packs_products'), {
            name: data.name || '', description: data.description || '',
            priceEur: Number(data.priceEur) || 0, price: data.price || '',
            imageUrl: data.imageUrl || '', images: data.images || [],
            category: data.category || '', includes: data.includes || [],
            stock: data.stock ?? -1, active: data.active !== false, featured: data.featured === true,
            order: data.order ?? 0, createdAt: serverTimestamp(),
        });
        return { success: true, message: 'Producto creado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo crear: ${e.message}` };
    }
}

export async function updatePackProduct(id: string, data: Partial<PackProduct>) {
    try {
        await updateDoc(doc(clientDb, 'packs_products', id), { ...data, updatedAt: serverTimestamp() });
        return { success: true, message: 'Producto actualizado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo actualizar: ${e.message}` };
    }
}

export async function deletePackProduct(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'packs_products', id));
        return { success: true, message: 'Producto eliminado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo eliminar: ${e.message}` };
    }
}

// ── Ajustes ──
// Los públicos (abierta/cerrada, aviso, términos) van en `app_settings/tienda`;
// los datos de cobro reales viven aparte en `packs_config/main`, que solo lee
// el admin (y las CF, para servírselos al dueño de un pedido aceptado).

export async function getPacksShopConfig(): Promise<PacksShopConfig> {
    try {
        const snap = await getDoc(doc(clientDb, 'app_settings', 'tienda'));
        return snap.exists() ? (sanitizeFirestoreData(snap.data()) as PacksShopConfig) : {};
    } catch { return {}; }
}

export async function savePacksShopConfig(data: PacksShopConfig) {
    try {
        await setDoc(doc(clientDb, 'app_settings', 'tienda'), { ...data, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Ajustes guardados.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo guardar: ${e.message}` };
    }
}

export async function getPacksPaymentConfig(): Promise<PacksPaymentInfo> {
    try {
        const snap = await getDoc(doc(clientDb, 'packs_config', 'main'));
        return snap.exists() ? (sanitizeFirestoreData(snap.data()) as PacksPaymentInfo) : {};
    } catch { return {}; }
}

export async function savePacksPaymentConfig(data: PacksPaymentInfo) {
    try {
        await setDoc(doc(clientDb, 'packs_config', 'main'), { ...data, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Datos de cobro guardados.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo guardar: ${e.message}` };
    }
}

// ── Pedidos (todo por Cloud Function) ──

export const packsShop_createRequest = async (data: {
    productId: string; hermandadId: string; message: string; images: string[]; acceptedTerms: boolean;
}) => {
    const fn = httpsCallable(functions, 'packsShop_createRequest');
    try {
        const r = await fn(data);
        return r.data as { success: boolean; orderId: string; code: string; message: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo enviar la solicitud.') } as any;
    }
};

export const packsShop_reviewRequest = async (orderId: string, decision: 'accept' | 'reject', reason?: string) => {
    const fn = httpsCallable(functions, 'packsShop_reviewRequest');
    try {
        const r = await fn({ orderId, decision, reason: reason || '' });
        return r.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo revisar la solicitud.') } as any;
    }
};

export const packsShop_getPaymentInfo = async (orderId: string) => {
    const fn = httpsCallable(functions, 'packsShop_getPaymentInfo');
    try {
        const r = await fn({ orderId });
        return r.data as { success: boolean; info: PacksPaymentInfo; concept: string; amount: number };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudieron obtener los datos de pago.') } as any;
    }
};

export const packsShop_reportPayment = async (orderId: string, method: PackPaymentMethod) => {
    const fn = httpsCallable(functions, 'packsShop_reportPayment');
    try {
        const r = await fn({ orderId, method });
        return r.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo comunicar el pago.') } as any;
    }
};

export const packsShop_adminSetStatus = async (orderId: string, status: 'paid' | PackOrderStatus, note?: string) => {
    const fn = httpsCallable(functions, 'packsShop_adminSetStatus');
    try {
        const r = await fn({ orderId, status, note: note || '' });
        return r.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo cambiar el estado.') } as any;
    }
};

export const packsShop_sendMessage = async (orderId: string, text: string, images: string[] = []) => {
    const fn = httpsCallable(functions, 'packsShop_sendMessage');
    try {
        const r = await fn({ orderId, text, images });
        return r.data as { success: boolean; message?: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo enviar el mensaje.') } as any;
    }
};

// ── Contabilidad (packs_ledger / packs_expenses) ──
// Las dos colecciones son solo-admin, así que el panel escribe DIRECTO con la
// regla `isAdmin()`. Lo único que va por Cloud Function es el apunte automático
// al confirmar un cobro, que lo hace `packsShop_adminSetStatus`.

export async function createLedgerEntry(data: Partial<PackLedgerEntry>) {
    try {
        await addDoc(collection(clientDb, 'packs_ledger'), {
            fechaPago: data.fechaPago || serverTimestamp(),
            code: data.code || '',
            cliente: data.cliente || '',
            cofradia: data.cofradia || '',
            metodo: data.metodo || 'otro',
            producto: data.producto || '',
            precio: Number(data.precio) || 0,
            estado: data.estado || 'en_proceso',
            manual: true,
            createdAt: serverTimestamp(),
        });
        return { success: true, message: 'Apunte añadido.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo añadir: ${e.message}` };
    }
}

export async function updateLedgerEntry(id: string, data: Partial<PackLedgerEntry>) {
    try {
        await updateDoc(doc(clientDb, 'packs_ledger', id), { ...data, updatedAt: serverTimestamp() });
        return { success: true, message: 'Apunte actualizado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo actualizar: ${e.message}` };
    }
}

export async function deleteLedgerEntry(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'packs_ledger', id));
        return { success: true, message: 'Apunte eliminado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo eliminar: ${e.message}` };
    }
}

export async function createExpense(data: Partial<PackExpense>) {
    try {
        await addDoc(collection(clientDb, 'packs_expenses'), {
            concepto: data.concepto || '',
            precio: Number(data.precio) || 0,
            fecha: data.fecha || serverTimestamp(),
            createdAt: serverTimestamp(),
        });
        return { success: true, message: 'Gasto añadido.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo añadir: ${e.message}` };
    }
}

export async function updateExpense(id: string, data: Partial<PackExpense>) {
    try {
        await updateDoc(doc(clientDb, 'packs_expenses', id), { ...data, updatedAt: serverTimestamp() });
        return { success: true, message: 'Gasto actualizado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo actualizar: ${e.message}` };
    }
}

export async function deleteExpense(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'packs_expenses', id));
        return { success: true, message: 'Gasto eliminado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo eliminar: ${e.message}` };
    }
}

export const packsShop_markOrderSeen = async (orderId: string, as: 'user' | 'admin' = 'user') => {
    const fn = httpsCallable(functions, 'packsShop_markOrderSeen');
    try {
        const r = await fn({ orderId, as });
        return r.data as { success: boolean };
    } catch { return { success: false } as any; }
};

// ════════════════════════════════════════════════════════════════════
// ── EL JARTI INGLÉS (jarti_products / jarti_orders) ──
// ════════════════════════════════════════════════════════════════════
// Los productos los escribe el admin directamente (reglas isAdmin), igual que
// packs_products. TODO lo que toca dinero o pedidos va por Cloud Function.

export async function uploadJartiImage(file: File): Promise<{ success: boolean; url?: string; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const path = `jartiingles/${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, CACHE_SUBIDAS);
        const url = await getDownloadURL(storageRef);
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la imagen: ${e.message}` };
    }
}

export async function createJartiProduct(data: Partial<JartiProduct>) {
    try {
        await addDoc(collection(clientDb, 'jarti_products'), {
            name: data.name || '',
            description: data.description || '',
            price: Number(data.price) || 0,
            imageUrl: data.imageUrl || '',
            images: data.images || [],
            category: data.category || '',
            items: data.items || [],
            stock: Number.isFinite(Number(data.stock)) ? Number(data.stock) : -1,
            maxPerOrder: Number(data.maxPerOrder) || 0,
            active: data.active !== false,
            featured: data.featured === true,
            order: data.order ?? 0,
            createdAt: serverTimestamp(),
        });
        return { success: true, message: 'Producto creado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo crear: ${e.message}` };
    }
}

export async function updateJartiProduct(id: string, data: Partial<JartiProduct>) {
    try {
        await updateDoc(doc(clientDb, 'jarti_products', id), { ...data, updatedAt: serverTimestamp() });
        return { success: true, message: 'Producto actualizado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo actualizar: ${e.message}` };
    }
}

export async function deleteJartiProduct(id: string) {
    try {
        await deleteDoc(doc(clientDb, 'jarti_products', id));
        return { success: true, message: 'Producto eliminado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo eliminar: ${e.message}` };
    }
}

export async function getJartiShopConfig(): Promise<JartiShopConfig> {
    try {
        const snap = await getDoc(doc(clientDb, 'jarti_config', 'main'));
        return snap.exists() ? (sanitizeFirestoreData(snap.data()) as JartiShopConfig) : {};
    } catch { return {}; }
}

export async function setJartiShopConfig(data: JartiShopConfig) {
    try {
        await setDoc(doc(clientDb, 'jarti_config', 'main'), { ...data, updatedAt: serverTimestamp() }, { merge: true });
        return { success: true, message: 'Ajustes guardados.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo guardar: ${e.message}` };
    }
}

/** Catálogo de ítems que reporta el servidor (para el editor de productos). */
export async function getModItemCatalog(): Promise<{ items: JartiCatalogItem[]; updatedAt?: any }> {
    try {
        const snap = await getDoc(doc(clientDb, 'mod_item_catalog', 'main'));
        if (!snap.exists()) return { items: [] };
        const d = sanitizeFirestoreData(snap.data()) as any;
        return { items: (d.items || []) as JartiCatalogItem[], updatedAt: d.updatedAt };
    } catch { return { items: [] }; }
}

/** Resuelve el personaje de Minecraft del usuario + estado de la tienda. */
export const jartiShop_getIdentity = async () => {
    const fn = httpsCallable(functions, 'jartiShop_getIdentity');
    try {
        const r = await fn({});
        return r.data as { success: boolean; identity: JartiMcIdentity; config: JartiShopConfig };
    } catch (e: any) {
        return { success: false, identity: { found: false, message: e.message }, config: {} } as any;
    }
};

export const jartiShop_createOrder = async (accountId: string, lines: { productId: string; qty: number }[]) => {
    const fn = httpsCallable(functions, 'jartiShop_createOrder');
    try {
        const r = await fn({ accountId, lines });
        return r.data as { success: boolean; orderId: string; code: string; total: number; readyAtMs: number; message: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo completar el pedido.') } as any;
    }
};

export const jartiShop_cancelOrder = async (orderId: string) => {
    const fn = httpsCallable(functions, 'jartiShop_cancelOrder');
    try {
        const r = await fn({ orderId });
        return r.data as { success: boolean; refunded: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo cancelar.') } as any;
    }
};

export const jartiShop_adminOrderAction = async (orderId: string, action: 'ready' | 'deliver') => {
    const fn = httpsCallable(functions, 'jartiShop_adminOrderAction');
    try {
        const r = await fn({ orderId, action });
        return r.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: String(e.message || 'No se pudo aplicar.') } as any;
    }
};

// ── Admin: migración de saldos legacy (balance → oro) ──
export const admin_migrateBalanceToOro = async () => {
    const fn = httpsCallable(functions, 'admin_migrateBalanceToOro');
    try {
        const result = await fn({});
        return result.data as { success: boolean; migrated: number; skipped: number };
    } catch (e: any) {
        return { success: false, migrated: 0, skipped: 0, message: e.message } as any;
    }
};

// ── Admin: gestión de cuentas bancarias ──
export const admin_getAllAccounts = async () => {
    const fn = httpsCallable(functions, 'admin_getAllAccounts');
    try {
        const result = await fn({});
        return result.data as { success: boolean; accounts: any[] };
    } catch (e: any) {
        return { success: false, accounts: [] };
    }
};

export const admin_updateAccount = async (accountId: string, updates: Record<string, any>) => {
    const fn = httpsCallable(functions, 'admin_updateAccount');
    try {
        const result = await fn({ accountId, updates });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const admin_deleteAccount = async (accountId: string) => {
    const fn = httpsCallable(functions, 'admin_deleteAccount');
    try {
        const result = await fn({ accountId });
        return result.data as { success: boolean; message: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const admin_createAccount = async (data: { ownerId: string; accountName: string; pin: string; oro?: number; plata?: number; bronce?: number; accountType?: string }) => {
    const fn = httpsCallable(functions, 'admin_createAccount');
    try {
        const result = await fn(data);
        return result.data as { success: boolean; message: string; accountId?: string };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const admin_issueFine = async (data: { accountId: string; amount: number; currency?: string; reason: string }) => {
    const fn = httpsCallable(functions, 'admin_issueFine');
    try {
        const result = await fn(data);
        return result.data as { success: boolean; message: string; collected?: number };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

// ── JartiBet (casino) ──
export type RouletteBet = { kind: string; value?: number; numbers?: number[]; amount: number };

export type CasinoPlayerStats = { id: string; nickname: string; chips: number; netTotal: number; netRoulette: number; netMegafire: number; netBets: number; totalWagered: number; spins: number };
export const admin_getCasinoStats = async () => {
    const fn = httpsCallable(functions, 'admin_getCasinoStats');
    try {
        const result = await fn({});
        return result.data as { success: boolean; houseNet: number; totalWagered: number; players: CasinoPlayerStats[] };
    } catch (e: any) {
        return { success: false, houseNet: 0, totalWagered: 0, players: [] };
    }
};

// ── Apuestas deportivas ──
export const getSportsMatches = async () => {
    const fn = httpsCallable(functions, 'getSportsMatches');
    try { const r = await fn({}); return r.data as { success: boolean; matches: SportsMatch[] }; }
    catch (e: any) { return { success: false, matches: [] }; }
};

export const placeSportsBet = async (matchId: string, selection: 'home' | 'draw' | 'away', stake: number) => {
    const fn = httpsCallable(functions, 'placeSportsBet');
    try { const r = await fn({ matchId, selection, stake }); return r.data as { success: boolean; message: string }; }
    catch (e: any) { return { success: false, message: e.message }; }
};

export const getMySportsBets = async () => {
    const fn = httpsCallable(functions, 'getMySportsBets');
    try { const r = await fn({}); return r.data as { success: boolean; bets: SportsBet[] }; }
    catch (e: any) { return { success: false, bets: [] }; }
};

export const admin_createSportsMatch = async (data: Partial<SportsMatch>) => {
    const fn = httpsCallable(functions, 'admin_createSportsMatch');
    try { const r = await fn(data); return r.data as { success: boolean; message: string; id?: string }; }
    catch (e: any) { return { success: false, message: e.message }; }
};

export const admin_updateSportsMatch = async (matchId: string, updates: Partial<SportsMatch>) => {
    const fn = httpsCallable(functions, 'admin_updateSportsMatch');
    try { const r = await fn({ matchId, updates }); return r.data as { success: boolean; message: string }; }
    catch (e: any) { return { success: false, message: e.message }; }
};

export const admin_deleteSportsMatch = async (matchId: string) => {
    const fn = httpsCallable(functions, 'admin_deleteSportsMatch');
    try { const r = await fn({ matchId }); return r.data as { success: boolean; message: string }; }
    catch (e: any) { return { success: false, message: e.message }; }
};

export const admin_settleSportsMatch = async (matchId: string, result: 'home' | 'draw' | 'away') => {
    const fn = httpsCallable(functions, 'admin_settleSportsMatch');
    try { const r = await fn({ matchId, result }); return r.data as { success: boolean; message: string }; }
    catch (e: any) { return { success: false, message: e.message }; }
};

export type MatchBet = {
    id: string; userId: string; nickname: string;
    selection: 'home' | 'draw' | 'away'; team1: string; team2: string;
    odds: number; stake: number; potentialPayout: number; payout: number;
    status: 'pending' | 'won' | 'lost'; placedAt?: string;
};
export const admin_getMatchBets = async (matchId: string) => {
    const fn = httpsCallable(functions, 'admin_getMatchBets');
    try {
        const r = await fn({ matchId });
        return r.data as { success: boolean; bets: MatchBet[]; totalStake: number; totalPaid: number; playersNet: number };
    } catch (e: any) {
        return { success: false, bets: [] as MatchBet[], totalStake: 0, totalPaid: 0, playersNet: 0, message: e.message };
    }
};

export const admin_linkPlayerAccount = async (activityId: string, uid: string | null) => {
    const fn = httpsCallable(functions, 'admin_linkPlayerAccount');
    try { const r = await fn({ activityId, uid }); return r.data as { success: boolean; message: string }; }
    catch (e: any) { return { success: false, message: e.message }; }
};

export type AutoLinkMatch = { activityId: string; name: string; uid: string; nickname: string; source: 'formulario' | 'perfil' | 'mc_uuid' };
export type AutoLinkAmbiguous = { activityId: string; name: string; uids: { uid: string; nickname: string }[] };

/** Cruza los jugadores sin cuenta con los formularios de acceso; con `apply` los vincula. */
export const admin_autoLinkPlayers = async (apply: boolean) => {
    const fn = httpsCallable(functions, 'admin_autoLinkPlayers', { timeout: 120000 });
    try {
        const r = await fn({ apply });
        return r.data as { success: true; applied: boolean; matches: AutoLinkMatch[]; ambiguous: AutoLinkAmbiguous[] };
    } catch (e: any) {
        return { success: false as const, message: e.message as string, matches: [] as AutoLinkMatch[], ambiguous: [] as AutoLinkAmbiguous[] };
    }
};

export type UserNotInServer = {
    uid: string;
    nickname: string;
    email: string;
    discordId: string;
    minecraftNames: string[];
    banned: boolean;
    createdAt: number | null;
    worlds: { id: string; account: string; worldName: string; authorized: boolean }[];
};

/** Usuarios de Jartinet que nunca han entrado al servidor. */
export const admin_getUsersNotInServer = async () => {
    const fn = httpsCallable(functions, 'admin_usersNotInServer', { timeout: 300000 });
    try {
        const r = await fn({});
        return r.data as { success: true; users: UserNotInServer[] };
    } catch (e: any) {
        return { success: false as const, message: e.message as string, users: [] as UserNotInServer[] };
    }
};

/** Borra esos usuarios (Auth + perfil) y hace lockdown a sus mundos privados. */
export const admin_purgeUsersNotInServer = async (uids: string[], killTitle: string, killMessage: string) => {
    const fn = httpsCallable(functions, 'admin_usersNotInServer', { timeout: 300000 });
    try {
        const r = await fn({ purge: true, uids, killTitle, killMessage });
        return r.data as { success: true; deleted: number; lockedWorlds: number; errors: string[] };
    } catch (e: any) {
        return { success: false as const, message: e.message as string, deleted: 0, lockedWorlds: 0, errors: [] as string[] };
    }
};

import type { VotacionPublica } from '@/lib/types';

// ── Votaciones de hermandad (CF `votaciones`, una sola con `accion`) ──
const llamarVotaciones = async (data: Record<string, unknown>): Promise<{ success: boolean; message?: string; data?: Record<string, unknown> }> => {
    const fn = httpsCallable(functions, 'votaciones', { timeout: 120000 });
    try {
        const r = await fn(data);
        return { success: true, data: r.data as Record<string, unknown> };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const crearVotacion = (hermandadId: string, titulo: string, descripcion: string, opciones: string[]) =>
    llamarVotaciones({ accion: 'crear', hermandadId, titulo, descripcion, opciones });

export const empezarVotacion = (hermandadId: string, votacionId: string) =>
    llamarVotaciones({ accion: 'empezar', hermandadId, votacionId });

export const finalizarVotacion = (hermandadId: string, votacionId: string) =>
    llamarVotaciones({ accion: 'finalizar', hermandadId, votacionId });

export const eliminarVotacion = (hermandadId: string, votacionId: string) =>
    llamarVotaciones({ accion: 'eliminar', hermandadId, votacionId });

export const votarVotacion = (hermandadId: string, votacionId: string, opcionId: string) =>
    llamarVotaciones({ accion: 'votar', hermandadId, votacionId, opcionId });

export const verVotacion = async (hermandadId: string, votacionId: string) => {
    const r = await llamarVotaciones({ accion: 'ver', hermandadId, votacionId });
    return { success: r.success, message: r.message, votacion: (r.data?.votacion as VotacionPublica | undefined) || null };
};

export const casinoGetWallet = async () => {
    const fn = httpsCallable(functions, 'casinoGetWallet');
    try {
        const result = await fn({});
        return result.data as { success: boolean; chips: number };
    } catch (e: any) {
        return { success: false, chips: 0 };
    }
};

export const casinoDeposit = async (accountId: string, amount: number) => {
    const fn = httpsCallable(functions, 'casinoDeposit');
    try {
        const result = await fn({ accountId, amount });
        return result.data as { success: boolean; chips: number; message: string };
    } catch (e: any) {
        return { success: false, chips: 0, message: e.message };
    }
};

export const casinoWithdraw = async (accountId: string, amount: number) => {
    const fn = httpsCallable(functions, 'casinoWithdraw');
    try {
        const result = await fn({ accountId, amount });
        return result.data as { success: boolean; chips: number; message: string };
    } catch (e: any) {
        return { success: false, chips: 0, message: e.message };
    }
};

export const casinoSpinRoulette = async (bets: RouletteBet[]) => {
    const fn = httpsCallable(functions, 'casinoSpinRoulette');
    try {
        const result = await fn({ bets });
        return result.data as {
            success: boolean; number: number; color: 'red' | 'black' | 'green';
            totalBet: number; totalPayout: number; net: number; chips: number;
            results: { kind: string; value: number | null; amount: number; won: boolean; payout: number }[];
            message?: string;
        };
    } catch (e: any) {
        return { success: false, message: e.message } as any;
    }
};

export type FireBonusOrb = { col: number; row: number; value: number };
export type FireBonusEvent = { orbs: FireBonusOrb[]; spinsRemaining: number; filledCols: number[] };
export type FireBonusTop = { type: 'mult'; value: number } | { type: 'jackpot'; tier: string; value: number };
export type FireBonus = {
    tops: FireBonusTop[];
    events: FireBonusEvent[];
    columns: { top: FireBonusTop; sum: number; filled: boolean; cells: (number | null)[] }[];
    finalMultiplier: number;
};

export const casinoSpinMegaFire = async (bets: RouletteBet[]) => {
    const fn = httpsCallable(functions, 'casinoSpinMegaFire');
    try {
        const result = await fn({ bets });
        return result.data as {
            success: boolean; mode: 'megafire'; number: number; color: 'red' | 'black' | 'green';
            fireNumbers: number[]; mega: boolean; isFire: boolean; bonusTriggered: boolean; bonus: FireBonus | null; bonusBase: number;
            totalBet: number; totalPayout: number; net: number; chips: number;
            results: { kind: string; value: number | null; amount: number; won: boolean; payout: number; fireBoost?: boolean; bonus?: boolean }[];
            message?: string;
        };
    } catch (e: any) {
        return { success: false, message: e.message } as any;
    }
};

// ── Notificaciones (contadores por sección) ──
export type NotificationCounts = {
    social: number; avisos: number; periodico: number; mod: number;
    banco: number; hermandades: number; bandas: number; tienda: number;
};

export const getNotificationCounts = async () => {
    const fn = httpsCallable(functions, 'getNotificationCounts');
    try {
        const result = await fn({});
        return result.data as { success: boolean; counts: NotificationCounts };
    } catch (e: any) {
        return { success: false, counts: { social: 0, avisos: 0, periodico: 0, mod: 0, banco: 0, hermandades: 0, bandas: 0, tienda: 0 } };
    }
};

export const markSectionSeen = async (section: string) => {
    const fn = httpsCallable(functions, 'markSectionSeen');
    try {
        await fn({ section });
        return { success: true };
    } catch (e: any) {
        return { success: false };
    }
};

// ── Editores autorizados del Periódico (app_settings/periodico.editores) ──
export async function getPeriodicoEditors(): Promise<string[]> {
    try {
        const snap = await getDoc(doc(clientDb, 'app_settings', 'periodico'));
        if (!snap.exists()) return [];
        return (snap.data()?.editores as string[]) || [];
    } catch (e) {
        console.error('getPeriodicoEditors:', e);
        return [];
    }
}

export async function setPeriodicoEditors(editores: string[]): Promise<{ success: boolean; message: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        await setDoc(doc(clientDb, 'app_settings', 'periodico'), { editores }, { merge: true });
        return { success: true, message: 'Editores actualizados.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo guardar: ${e.message}` };
    }
}

// Sube una imagen a Cloud Storage y devuelve su URL pública. Usado por el
// editor de Jarticards (foto de la carta, imagen del sobre).
export const uploadJarticardImage = async (file: File): Promise<{ success: boolean; url?: string; message?: string }> => {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        file = await paraSubir(file);
        const path = `jarticards/${Date.now()}_${file.name.replace(/[^\w.\-]/g, '_')}`;
        const url = await subirImagenConMiniatura(path, file);
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la imagen: ${e.message}` };
    }
};

// ─── GALERÍA PÚBLICA ────────────────────────────────────────────────────────
// Eventos de la galería (`gallery_events`), gestionados desde el panel admin.
// Escritura directa desde el cliente (regla `isAdmin()` en Firestore), igual
// que el mapa de la ciudad o los packs: no hace falta Cloud Function.

/** Sube una foto de galería a Storage y devuelve su URL pública. */
export async function uploadGalleryImage(file: File | Blob, name?: string): Promise<{ success: boolean; url?: string; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        // Aquí puede llegar un Blob (pegado con Ctrl+V), no solo un File.
        const listo = await comprimirImagen(file);
        const bruto = name && listo === file
            ? name
            : (listo as File).name || name || (file as File).name || 'foto.webp';
        const safe = bruto.replace(/[^\w.\-]/g, '_');
        const storageRef = ref(storage, `gallery/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safe}`);
        await uploadBytes(storageRef, listo, CACHE_SUBIDAS);
        const url = await getDownloadURL(storageRef);
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: `Error al subir la imagen: ${e.message}` };
    }
}

export async function getGalleryEvents(): Promise<GalleryEventDoc[]> {
    try {
        const snap = await getDocs(collection(clientDb, 'gallery_events'));
        return snap.docs
            .map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() }) as GalleryEventDoc)
            .sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
    } catch {
        return [];
    }
}

export async function createGalleryEvent(data: { title: string; date: string; modVersion?: string; order?: number }): Promise<{ success: boolean; id?: string; message: string }> {
    const user = mainAuth.currentUser;
    if (!user || !(await isClientAdmin(user.uid))) return { success: false, message: 'No autorizado.' };
    try {
        const docRef = await addDoc(collection(clientDb, 'gallery_events'), {
            title: data.title,
            date: data.date,
            modVersion: data.modVersion || '',
            order: data.order ?? Date.now(),
            images: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        return { success: true, id: docRef.id, message: 'Evento creado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo crear: ${e.message}` };
    }
}

export async function updateGalleryEvent(id: string, updates: Partial<Omit<GalleryEventDoc, 'id'>>): Promise<{ success: boolean; message: string }> {
    const user = mainAuth.currentUser;
    if (!user || !(await isClientAdmin(user.uid))) return { success: false, message: 'No autorizado.' };
    try {
        await updateDoc(doc(clientDb, 'gallery_events', id), { ...updates, updatedAt: serverTimestamp() } as any);
        return { success: true, message: 'Evento actualizado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo guardar: ${e.message}` };
    }
}

export async function deleteGalleryEvent(id: string): Promise<{ success: boolean; message: string }> {
    const user = mainAuth.currentUser;
    if (!user || !(await isClientAdmin(user.uid))) return { success: false, message: 'No autorizado.' };
    try {
        await deleteDoc(doc(clientDb, 'gallery_events', id));
        return { success: true, message: 'Evento eliminado.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo eliminar: ${e.message}` };
    }
}

// ─── ESTAMPITAS DEL MOD ─────────────────────────────────────────────────────
// Gestionadas desde /jartinet/admin/estampitas. Escritura directa del cliente
// con la regla `isAdmin()` (patrón de la galería y del mapa): no hace falta
// Cloud Function para el CRUD.
//
// Lo que SÍ va por Cloud Function es reconstruir `estampitas/manifest.json`
// (trigger `estampitas_onChanged`), que es el contrato con el mod: así el
// manifiesto no puede quedarse desincronizado ni aunque alguien toque Firestore
// desde la consola de Firebase.

/**
 * Tamaño FIJO de la estampita. No es un máximo: el admin recuadra la imagen
 * dentro de este marco (2:3, que es la forma de una estampita de verdad), así
 * que **todas salen con la misma proporción** pase lo que pase con el original.
 *
 * Esto es lo que evita el problema de subir una foto apaisada y que en el juego
 * se vea apaisada: el encuadre lo decide una persona, no un `object-fit`.
 */
export const ESTAMPITA_ANCHO = 512;
export const ESTAMPITA_ALTO = 768;

/** Encuadre de la imagen dentro del marco, en píxeles del marco final. */
export interface EncuadreEstampita {
    /** Escala: píxeles del marco por píxel de la imagen. */
    escala: number;
    /** Centro de la imagen dentro del marco. */
    cx: number;
    cy: number;
    /** Color de fondo (lo que se ve donde la imagen no llega). */
    fondo: string;
}

/**
 * Pinta la imagen recuadrada en un canvas del tamaño que se pida, aplicando el
 * encuadre proporcionalmente.
 *
 * La MISMA función dibuja la vista previa del panel y el PNG que se sube, solo
 * que con distinto `ancho`: por eso lo que se ve en el editor es exactamente lo
 * que acaba en el juego, sin sorpresas de redondeo.
 */
export function pintarEstampita(
    ctx: CanvasRenderingContext2D,
    img: CanvasImageSource,
    iw: number,
    ih: number,
    enc: EncuadreEstampita,
    ancho: number,
): void {
    const k = ancho / ESTAMPITA_ANCHO;
    const alto = Math.round(ESTAMPITA_ALTO * k);
    ctx.clearRect(0, 0, ancho, alto);
    ctx.fillStyle = enc.fondo;
    ctx.fillRect(0, 0, ancho, alto);
    ctx.imageSmoothingQuality = 'high';
    const w = iw * enc.escala * k;
    const h = ih * enc.escala * k;
    ctx.drawImage(img, enc.cx * k - w / 2, enc.cy * k - h / 2, w, h);
}

/**
 * Exporta la estampita final a **PNG** de 512×768.
 *
 * Se hace AQUÍ, en el navegador, y no en una Cloud Function: no añade ninguna
 * dependencia al backend y evita que alguien suba un JPG de 8 MB que luego
 * tendrían que bajarse todos los jugadores.
 *
 * **Tiene que ser PNG**: el mod la lee con `NativeImage.read`, que no entiende
 * WebP ni JPG por muy pequeños que sean.
 */
export async function exportarEstampita(
    img: CanvasImageSource,
    iw: number,
    ih: number,
    enc: EncuadreEstampita,
): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = ESTAMPITA_ANCHO;
    canvas.height = ESTAMPITA_ALTO;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('El navegador no permite componer la imagen.');
    pintarEstampita(ctx, img, iw, ih, enc, ESTAMPITA_ANCHO);
    return new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No se pudo convertir a PNG.'))), 'image/png');
    });
}

/** SHA-256 (16 caracteres) del contenido: es el nombre del fichero en Storage. */
async function hashDeBlob(blob: Blob): Promise<string> {
    const buf = await blob.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, 16);
}

/**
 * Sube la imagen de una estampita ya normalizada. **Direccionada por
 * contenido**: el fichero se llama por el hash de su PNG, así que volver a
 * subir la misma imagen no ocupa el doble y —lo importante— una estampita que
 * no cambia NO se vuelve a descargar en el juego.
 */
export async function uploadEstampitaImage(
    blob: Blob,
): Promise<{ success: boolean; url?: string; hash?: string; bytes?: number; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const hash = await hashDeBlob(blob);
        const storageRef = ref(storage, `estampitas/img/${hash}.png`);
        await uploadBytes(storageRef, blob, { contentType: 'image/png', cacheControl: 'public, max-age=31536000, immutable' });
        const url = await getDownloadURL(storageRef);
        return { success: true, url, hash, bytes: blob.size };
    } catch (e: any) {
        return { success: false, message: `Error al subir la imagen: ${e.message}` };
    }
}

export async function getEstampitas(): Promise<EstampitaDoc[]> {
    try {
        const snap = await getDocs(collection(clientDb, 'estampitas'));
        return snap.docs
            .map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() }) as EstampitaDoc)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    } catch {
        return [];
    }
}

export async function createEstampita(
    data: { nombre: string; imageUrl: string; hash: string; w?: number; h?: number; order?: number },
): Promise<{ success: boolean; id?: string; message: string }> {
    const user = mainAuth.currentUser;
    if (!user || !(await isClientAdmin(user.uid))) return { success: false, message: 'No autorizado.' };
    try {
        const docRef = await addDoc(collection(clientDb, 'estampitas'), {
            nombre: data.nombre,
            imageUrl: data.imageUrl,
            hash: data.hash,
            w: data.w ?? 0,
            h: data.h ?? 0,
            order: data.order ?? Date.now(),
            activa: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        return { success: true, id: docRef.id, message: 'Estampita creada.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo crear: ${e.message}` };
    }
}

export async function updateEstampita(
    id: string,
    updates: Partial<Omit<EstampitaDoc, 'id'>>,
): Promise<{ success: boolean; message: string }> {
    const user = mainAuth.currentUser;
    if (!user || !(await isClientAdmin(user.uid))) return { success: false, message: 'No autorizado.' };
    try {
        await updateDoc(doc(clientDb, 'estampitas', id), { ...updates, updatedAt: serverTimestamp() } as any);
        return { success: true, message: 'Estampita guardada.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo guardar: ${e.message}` };
    }
}

export async function deleteEstampita(id: string): Promise<{ success: boolean; message: string }> {
    const user = mainAuth.currentUser;
    if (!user || !(await isClientAdmin(user.uid))) return { success: false, message: 'No autorizado.' };
    try {
        await deleteDoc(doc(clientDb, 'estampitas', id));
        return { success: true, message: 'Estampita eliminada.' };
    } catch (e: any) {
        return { success: false, message: `No se pudo eliminar: ${e.message}` };
    }
}

/**
 * Estado del manifiesto que consume el mod (lo escribe la Cloud Function).
 * Sirve para que el panel enseñe si lo que hay publicado casa con lo que se ve.
 */
export async function getEstampitasManifestState(): Promise<{ count: number; hash: string; generatedAt: number } | null> {
    try {
        const snap = await getDoc(doc(clientDb, 'estampitas_render', 'main'));
        if (!snap.exists()) return null;
        return sanitizeFirestoreData(snap.data()) as any;
    } catch {
        return null;
    }
}

/** Fuerza la regeneración del manifiesto (por si un trigger se perdió). */
export async function regenerarManifiestoEstampitas(): Promise<{ success: boolean; message: string }> {
    try {
        const fn = httpsCallable(functions, 'estampitas_regenerar');
        const res: any = await fn({});
        return { success: true, message: `Manifiesto regenerado (${res.data?.count ?? 0} estampitas).` };
    } catch (e: any) {
        return { success: false, message: e.message || 'No se pudo regenerar.' };
    }
}

// ─── JARTIMARCHAS ───────────────────────────────────────────────────────────
// Las marchas que tocan las bandas dentro de Minecraft.
//
// Todo va por Cloud Function (a diferencia de las estampitas, que escriben
// directas): hay que comprobar que quien sube es DIRECTOR de esa banda y el
// audio se transcodifica en el servidor con ffmpeg. Lo único que hace el cliente
// contra Storage es dejar el fichero crudo en su carpeta de entrada.

/** Formatos que aceptamos como origen. El resultado siempre es OGG mono. */
export const MARCHA_FORMATOS = '.mp3,.wav,.m4a,.aac,.flac,.ogg,.opus,.webm,.mp4';
/** Tope del fichero de ENTRADA (el mismo que valida la función). */
export const MARCHA_MAX_ENTRADA = 60 * 1024 * 1024;

/** Todas las marchas (el panel filtra por banda o por estado en memoria). */
export async function getMarchas(): Promise<MarchaDoc[]> {
    try {
        const snap = await getDocs(collection(clientDb, 'marchas'));
        return snap.docs
            .map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() }) as MarchaDoc)
            .sort((a, b) => (a.bandaNombre || '').localeCompare(b.bandaNombre || '', 'es')
                || (a.repertorioItem || '').localeCompare(b.repertorioItem || '', 'es'));
    } catch {
        return [];
    }
}

/** Las de una banda concreta (igualdad sola → sin índice compuesto). */
export async function getMarchasDeBanda(bandaId: string): Promise<MarchaDoc[]> {
    try {
        const q = query(collection(clientDb, 'marchas'), where('bandaId', '==', bandaId));
        const snap = await getDocs(q);
        return snap.docs
            .map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() }) as MarchaDoc)
            .sort((a, b) => (a.repertorioItem || '').localeCompare(b.repertorioItem || '', 'es'));
    } catch {
        return [];
    }
}

/**
 * Sube el fichero crudo a la carpeta de entrada del usuario y lo transcodifica.
 *
 * <p>El crudo se borra en cuanto la función termina: lo que se conserva es el OGG
 * mono normalizado, direccionado por el hash de su contenido.</p>
 */
export async function procesarMarchaArchivo(
    bandaId: string,
    file: File,
): Promise<{ success: boolean; data?: MarchaProcesada; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    if (file.size > MARCHA_MAX_ENTRADA) {
        return { success: false, message: 'El archivo pasa de 60 MB. Comprímelo o recórtalo antes.' };
    }
    try {
        const limpio = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-80);
        const ruta = `marchas/entrada/${user.uid}/${Date.now()}-${limpio}`;
        await uploadBytes(ref(storage, ruta), file, { contentType: file.type || 'application/octet-stream' });

        const fn = httpsCallable(functions, 'marchas_procesar');
        const res: any = await fn({ bandaId, storagePath: ruta });
        return { success: true, data: res.data as MarchaProcesada };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo procesar el audio.' };
    }
}

/** Extrae el audio de un vídeo de YouTube y lo transcodifica. */
export async function procesarMarchaYoutube(
    bandaId: string,
    youtubeUrl: string,
): Promise<{ success: boolean; data?: MarchaProcesada; message?: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_procesar');
        const res: any = await fn({ bandaId, youtubeUrl });
        return { success: true, data: res.data as MarchaProcesada };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo extraer el audio de YouTube.' };
    }
}

/** Pide el alta de la marcha en el repertorio (queda pendiente de revisión). */
export async function solicitarMarcha(datos: {
    bandaId: string;
    nombre: string;
    categoria: MarchaCategoria;
    repertorioItem: string;
    audioUrl: string;
    hash: string;
    bytes: number;
    duracionMs: number;
    origen: MarchaOrigen;
    sourceUrl?: string;
}): Promise<{ success: boolean; estado?: MarchaEstado; message: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_solicitar');
        const res: any = await fn(datos);
        const estado = res.data?.estado as MarchaEstado;
        return {
            success: true,
            estado,
            message: estado === 'aceptada'
                ? 'Marcha añadida al repertorio.'
                : 'Solicitud enviada. Un administrador la revisará.',
        };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo enviar la solicitud.' };
    }
}

/** Aprobar o rechazar (solo admin). */
export async function revisarMarcha(
    id: string,
    accion: 'aceptada' | 'rechazada',
    motivo?: string,
): Promise<{ success: boolean; message: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_revisar');
        await fn({ id, accion, motivo: motivo || '' });
        return { success: true, message: accion === 'aceptada' ? 'Marcha aprobada.' : 'Marcha rechazada.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo revisar.' };
    }
}

/** Borra una marcha (admin, o director de su banda). */
export async function eliminarMarcha(id: string): Promise<{ success: boolean; message: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_eliminar');
        await fn({ id });
        return { success: true, message: 'Marcha eliminada.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo eliminar.' };
    }
}

/**
 * Guarda el RECORTE de una marcha: desde y hasta qué milisegundo suena en el juego
 * (`finMs` a 0 = hasta el final). Solo el director de su banda (o un admin).
 *
 * El audio NO se corta: son dos números que viajan en el manifiesto, y como el recorte
 * entra en la firma, el servidor del mod lo recoge en menos de 15 s sin reconectar.
 */
export async function recortarMarcha(
    id: string,
    iniMs: number,
    finMs: number,
): Promise<{ success: boolean; message: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_recorte');
        await fn({ id, iniMs, finMs });
        return { success: true, message: 'Recorte guardado. En el juego se nota en menos de 15 s.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo guardar el recorte.' };
    }
}

/** Estado del manifiesto que consume el mod. */
export async function getMarchasManifestState(): Promise<MarchasManifestState | null> {
    try {
        const snap = await getDoc(doc(clientDb, 'marchas_render', 'main'));
        if (!snap.exists()) return null;
        return sanitizeFirestoreData(snap.data()) as MarchasManifestState;
    } catch {
        return null;
    }
}

/** Regenera el manifiesto a mano (por si un trigger se perdió). */
export async function regenerarManifiestoMarchas(): Promise<{ success: boolean; message: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_regenerar');
        const res: any = await fn({});
        return {
            success: true,
            message: `Manifiesto regenerado (${res.data?.marchas ?? 0} marchas en ${res.data?.bandas ?? 0} bandas).`,
        };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo regenerar.' };
    }
}

/** Uso de almacenamiento de las marchas (solo admin). */
export async function getMarchasAlmacenamiento(): Promise<{
    ficheros: number; bytesStorage: number; bytesUsados: number; bandas: number;
    pendiente: number; aceptada: number; rechazada: number;
} | null> {
    try {
        const fn = httpsCallable(functions, 'marchas_almacenamiento');
        const res: any = await fn({});
        return res.data;
    } catch {
        return null;
    }
}

/** «3,4 MB» a partir de bytes. */
export function formatearBytes(bytes: number): string {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** «4:12» a partir de milisegundos. */
export function formatearDuracion(ms: number): string {
    const s = Math.max(0, Math.round((ms || 0) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ─── Sesiones de banda ──────────────────────────────────────────────────────

/** Las sesiones de una banda (igualdad sola → sin índice compuesto). */
export async function getSesionesDeBanda(bandaId: string): Promise<SesionBanda[]> {
    try {
        const q = query(collection(clientDb, 'marchas_sesiones'), where('bandaId', '==', bandaId));
        const snap = await getDocs(q);
        return snap.docs
            .map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() }) as SesionBanda)
            .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
    } catch {
        return [];
    }
}

export async function guardarSesion(datos: {
    id?: string;
    bandaId: string;
    nombre: string;
    pistas: SesionPista[];
}): Promise<{ success: boolean; id?: string; message: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_guardarSesion');
        const res: any = await fn(datos);
        return { success: true, id: res.data?.id, message: 'Sesión guardada.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo guardar la sesión.' };
    }
}

export async function borrarSesion(id: string): Promise<{ success: boolean; message: string }> {
    try {
        const fn = httpsCallable(functions, 'marchas_borrarSesion');
        await fn({ id });
        return { success: true, message: 'Sesión borrada.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo borrar.' };
    }
}

/** Ruta fija del Importador en Storage: el enlace repartido no caduca nunca. */
const IMPORTADOR_RUTA = 'importador/JartiMarchas.exe';

/**
 * Prepara el lanzamiento del Importador y devuelve la URL `jartimarchas://`.
 *
 * El programa necesita poder subir EN NOMBRE del director sin pedirle la
 * contraseña, así que la web le entrega su propio token de sesión a través de un
 * **código de un solo uso** que caduca en 15 minutos. En la URL solo viaja el
 * código, nunca el token: los argumentos de un proceso los puede leer cualquier
 * otro programa del mismo usuario.
 */
export async function abrirEnImportador(
    bandaId: string,
    youtubeUrl: string,
): Promise<{ success: boolean; url?: string; message?: string }> {
    const user = mainAuth.currentUser;
    if (!user) return { success: false, message: 'Debes iniciar sesión.' };
    try {
        const idToken = await user.getIdToken();
        const fn = httpsCallable(functions, 'marchas_emparejar');
        const res: any = await fn({ bandaId, idToken });
        const codigo = res.data?.codigo;
        if (!codigo) return { success: false, message: 'No se pudo preparar la sesión.' };
        const url = `jartimarchas://importar?url=${encodeURIComponent(youtubeUrl)}`
            + `&code=${encodeURIComponent(codigo)}`;
        return { success: true, url };
    } catch (e: any) {
        return { success: false, message: e?.message || 'No se pudo preparar la sesión.' };
    }
}

/**
 * Datos del Importador de JartiMarchas (el programa de escritorio que baja las
 * marchas de YouTube desde el ordenador del director).
 *
 * El tamaño y la fecha se leen del PROPIO fichero de Storage, no de un documento
 * de configuración: así, republicar una versión nueva es solo volver a subirla con
 * `importador/publicar.cjs`, sin tocar código ni acordarse de actualizar nada.
 */
export async function getImportadorInfo(): Promise<{ url: string; bytes: number; actualizado: string } | null> {
    try {
        const r = ref(storage, IMPORTADOR_RUTA);
        const [meta, url] = await Promise.all([getMetadata(r), getDownloadURL(r)]);
        return {
            url,
            bytes: Number(meta.size) || 0,
            actualizado: meta.updated || '',
        };
    } catch {
        // Si aún no se ha publicado, la tarjeta simplemente no enseña los detalles.
        return null;
    }
}
